/**
 * Financeiro A4 — abas Cartões e Reembolsos do Contas a Pagar (mockups C1–C4, R1, R2, P1 e P2 aprovados pela Valéria
 * em 20/10/2026) e os campos de "Quem pagou" (conta da Inttrix, cartão da Inttrix ou pessoa que pagou) usados em
 * Registrar pagamento e Nova despesa.
 *  - O cartão é meio de pagamento: a compra é a despesa (uma vez); a fatura é a dívida com o emissor; pagar a fatura
 *    é a única saída de caixa. Conferência com o emissor mostra a diferença e nada cria para fechar o total.
 *  - Pessoa que pagou: escolha obrigatória, sem padrão (gera reembolso, já reembolsado, não há valor a reembolsar).
 *  - Reembolso: agrupa o que se deve à mesma pessoa; o pagamento (uma saída) é distribuído por item.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ErroApi } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { Campo } from '../componentes/basicos.tsx';
import { Ajuda, CardLista, CardsLista, EstadoCarga, Modal, RolagemIndicada, SeloSituacao, useCarga, type Tom } from '../comercial/ui.tsx';
import { CampoData, hojeIso } from '../comercial/CampoData.tsx';
import { centavosDe, dataBr, deCentavos, MEIOS, mesExtenso, NovaDespesa, nomeBanco, novaChave, plural, rs, useApoio, type ContaBancaria } from './ContasPagar.tsx';

/* ---------- tipos da API ---------- */
interface Fatura {
  id: string;
  numero: string;
  obrigacaoId: string;
  cartao: { id: string; apelido: string; emissor: string; final: string };
  mes: string;
  fechamento: string;
  vencimento: string;
  compras: string;
  nCompras: number;
  encargos: string;
  creditos: string;
  cobrado: string;
  pago: string;
  saldo: string;
  saldoAnteriorVinculo: string;
  levadoAdiante: string;
  situacao: 'sem_lancamentos' | 'aberta' | 'fechada' | 'parcial' | 'paga';
  vencida: boolean;
  aConferir: number;
  conferencia: {
    totalEmissor: string;
    totalRegistrado: string;
    diferenca: string;
    observacao: string | null;
    em: string;
    por: string | null;
  } | null;
}
interface Cartao {
  id: string;
  apelido: string;
  emissor: string;
  final: string;
  diaFechamento: number;
  diaVencimento: number;
  ativo: boolean;
}
interface ListaCartoes {
  referencia: string;
  cartoes: Cartao[];
  resumo: {
    emAberto: { valor: string; quantidade: number };
    vencem7: {
      valor: string;
      quantidade: number;
      proxima: { mes: string; vencimento: string } | null;
    };
    aConferir: { quantidade: number };
    pagoNoMes: { valor: string; quantidade: number };
  };
  faturas: Fatura[];
}
interface FichaFatura {
  fatura: Fatura;
  compras: {
    pagamentoId: string;
    pagamento: string;
    data: string;
    obrigacaoId: string;
    obrigacao: string;
    despesa: string;
    descricao: string;
    favorecido: string;
    parcela: string | null;
    valor: string;
    situacao: 'sugerida' | 'conferida';
    justificativa: string | null;
  }[];
  lancamentos: {
    id: string;
    tipo: 'encargo' | 'credito' | 'devolucao';
    categoria: 'cashback' | null;
    valor: string;
    descricao: string;
    conta: string | null;
    compraOrigem: { obrigacao: string; descricao: string } | null;
    em: string;
    autor: string | null;
    anulacao: { em: string; justificativa: string } | null;
  }[];
  transportes: {
    id: string;
    desfeito: { em: string; justificativa: string; por: string | null } | null;
    sentido: 'saida' | 'entrada';
    origem: { numero: string; mes: string };
    destino: { numero: string; mes: string };
    valor: string;
    justificativa: string;
    em: string;
    autor: string | null;
  }[];
  pagamentos: {
    id: string;
    numero: string;
    data: string;
    valor: string;
    aplicado: string;
    conta: string | null;
    meio: string;
    identificador: string | null;
    estorno: { em: string; justificativa: string } | null;
  }[];
  conferencias: {
    totalEmissor: string;
    totalRegistrado: string;
    diferenca: string;
    observacao: string | null;
    em: string;
    autor: string | null;
  }[];
  faturasComSaldo: Fatura[];
  outrasFaturas: { numero: string; mes: string; situacao: string }[];
  comprasComCredito: {
    obrigacaoId: string;
    obrigacao: string;
    despesa: string;
    descricao: string;
    valorEfetivo: string;
    credito: string;
    liquidado: string;
    disponivel: string;
  }[];
  periodoFechado: boolean;
}
interface ItemPessoa {
  id: string;
  numero: string;
  data: string;
  valor: string;
  pessoa: { id: string; nome: string; comUsuario: boolean };
  obrigacao: string;
  despesa: string;
  descricao: string;
  favorecido: string;
  documento: string | null;
  comArquivo: boolean;
  escolha: 'gera' | 'sem';
  justificativaSemReembolso: string | null;
  reembolso: string | null;
}
interface Reembolso {
  id: string;
  numero: string;
  pessoa: { id: string; nome: string };
  itens: number;
  total: string;
  reembolsado: string;
  saldo: string;
  vencimento: string | null;
  situacao: 'aberto' | 'parcial' | 'pago' | 'cancelado';
}
interface ListaReembolsos {
  referencia: string;
  resumo: {
    aReembolsar: { valor: string; pessoas: number; quantidade: number };
    emAbertos: { valor: string; quantidade: number; numeros: string[] };
    reembolsadoNoMes: { valor: string; quantidade: number };
    semReembolso: { quantidade: number };
  };
  pessoas: {
    pessoa: { id: string; nome: string; comUsuario: boolean };
    aReembolsar: string;
    itens: ItemPessoa[];
  }[];
  semReembolso: ItemPessoa[];
  reembolsos: Reembolso[];
}
interface FichaReembolso {
  reembolso: Reembolso & {
    cancelamento: { em: string; motivo: string } | null;
  };
  itens: {
    id: string;
    pagamento: string;
    data: string;
    obrigacao: string;
    despesa: string;
    descricao: string;
    favorecido: string;
    valor: string;
    reembolsado: string;
    saldo: string;
  }[];
  pagamentos: {
    id: string;
    numero: string;
    data: string;
    valor: string;
    aplicado: string;
    conta: string | null;
    meio: string;
    identificador: string | null;
    estorno: { em: string; justificativa: string } | null;
    distribuicao: { itemId: string; valor: string }[];
    alocacaoId: string;
    desfeito: { em: string; justificativa: string; por: string | null } | null;
    compartilhado: boolean;
  }[];
}
interface Disponivel {
  id: string;
  numero: string;
  data: string;
  valor: string;
  disponivel: string;
  conta: string;
  meio: string;
  identificador: string | null;
}

/* ---------- formatos ---------- */
const mesCurto = (aaaaMm: string) => {
  const m = mesExtenso(aaaaMm);
  return `${m.slice(0, 3)}/${aaaaMm.slice(0, 4)}`;
};
const c = (v: string | number) => Math.round(Number(v) * 100);
const SITUACAO_FATURA: Record<Fatura['situacao'], { texto: string; tom: Tom }> = {
  sem_lancamentos: { texto: 'Sem lançamentos', tom: 'encerrado' },
  aberta: { texto: 'Aberta', tom: 'aberto' },
  fechada: { texto: 'Fechada', tom: 'aberto' },
  parcial: { texto: 'Parcial', tom: 'parcial' },
  paga: { texto: 'Paga', tom: 'positivo' },
};
const SITUACAO_RB: Record<Reembolso['situacao'], { texto: string; tom: Tom }> = {
  aberto: { texto: 'Em aberto', tom: 'aberto' },
  parcial: { texto: 'Parcial', tom: 'parcial' },
  pago: { texto: 'Pago', tom: 'positivo' },
  cancelado: { texto: 'Cancelado', tom: 'encerrado' },
};
const nomeCartao = (x: { apelido: string; final: string }) => `${x.apelido} · final ${x.final}`;
const ultimoDia = (a: number, m0: number) => new Date(Date.UTC(a, m0 + 1, 0)).getUTCDate();
/** Mesma regra do servidor: até o fechamento, a fatura do mês; depois, a seguinte (+ deslocamento). */
export function faturaSugerida(data: string, diaFech: number, diaVenc: number, deslocamento = 0) {
  const [a, m, d] = data.split('-').map(Number);
  const base = new Date(Date.UTC(a, m - 1 + (d > Math.min(diaFech, ultimoDia(a, m - 1)) ? 1 : 0) + deslocamento, 1));
  return datasDaFatura(base.toISOString().slice(0, 7), diaFech, diaVenc);
}
function datasDaFatura(mes: string, diaFech: number, diaVenc: number) {
  const [a, m] = mes.split('-').map(Number);
  const fech = new Date(Date.UTC(a, m - 1, Math.min(diaFech, ultimoDia(a, m - 1))));
  const vb = new Date(Date.UTC(a, m - 1 + (diaVenc > diaFech ? 0 : 1), 1));
  const venc = new Date(Date.UTC(vb.getUTCFullYear(), vb.getUTCMonth(), Math.min(diaVenc, ultimoDia(vb.getUTCFullYear(), vb.getUTCMonth()))));
  return {
    mes,
    fechamento: fech.toISOString().slice(0, 10),
    vencimento: venc.toISOString().slice(0, 10),
  };
}
const somarMes = (mes: string, k: number) => {
  const [a, m] = mes.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1 + k, 1)).toISOString().slice(0, 7);
};
const erroDe = (e: unknown) => (e as Error).message;

/* ====================================================================== Quem pagou (P1 / P2) */
export type TipoOrigem = 'conta' | 'cartao' | 'pessoa';
export type EscolhaReembolso = '' | 'gera' | 'ja_reembolsado' | 'sem';
export interface Origem {
  tipo: TipoOrigem;
  cartaoId: string;
  faturaMes: string;
  justificativaFatura: string;
  pessoa: { id: string; nome: string } | null;
  escolha: EscolhaReembolso;
  justificativaSem: string;
  jr: {
    modo: '' | 'vinculo' | 'novo';
    pagamentoId: string;
    data: string;
    contaId: string;
    meio: string;
    identificador: string;
    valor: string;
    confirmarDuplicidade: boolean;
    justificativaDuplicidade: string;
  };
}
export const origemInicial = (tipo: TipoOrigem = 'conta'): Origem => ({
  tipo,
  cartaoId: '',
  faturaMes: '',
  justificativaFatura: '',
  pessoa: null,
  escolha: '',
  justificativaSem: '',
  jr: {
    modo: '',
    pagamentoId: '',
    data: hojeIso(),
    contaId: '',
    meio: 'pix',
    identificador: '',
    valor: '',
    confirmarDuplicidade: false,
    justificativaDuplicidade: '',
  },
});

/** Campos que o servidor espera para cartão e pessoa (conta: os campos de sempre). */
export function payloadOrigem(o: Origem, valorPago: string) {
  if (o.tipo === 'cartao')
    return {
      origem: 'cartao',
      cartao_id: o.cartaoId,
      fatura_mes: o.faturaMes || undefined,
      justificativa_fatura: o.justificativaFatura || undefined,
    };
  if (o.tipo === 'pessoa')
    return {
      origem: 'pessoa',
      pessoa_id: o.pessoa?.id,
      reembolso_escolha: o.escolha || undefined,
      justificativa_sem_reembolso: o.escolha === 'sem' ? o.justificativaSem : undefined,
      reembolso:
        o.escolha === 'ja_reembolsado'
          ? o.jr.modo === 'vinculo'
            ? { vinculo: { pagamento_id: o.jr.pagamentoId } }
            : o.jr.modo === 'novo'
              ? {
                  novo: {
                    data_pagamento: o.jr.data,
                    conta_bancaria_id: o.jr.contaId,
                    meio: o.jr.meio,
                    identificador_transacao: o.jr.identificador || null,
                    valor: o.jr.valor || valorPago,
                    confirmar_sem_duplicidade: o.jr.confirmarDuplicidade,
                    justificativa_duplicidade: o.jr.confirmarDuplicidade ? o.jr.justificativaDuplicidade : undefined,
                  },
                }
              : undefined
          : undefined,
    };
  return { origem: 'conta' };
}
/** O que falta escolher antes de registrar (o botão fica desabilitado com a explicação). */
export function pendenciaOrigem(o: Origem): string | null {
  if (o.tipo === 'cartao') {
    if (!o.cartaoId) return 'Escolha o cartão da Inttrix.';
    return null;
  }
  if (o.tipo === 'pessoa') {
    if (!o.pessoa) return 'Escolha a pessoa que pagou.';
    if (!o.escolha) return 'Escolha o que acontece com o reembolso (nenhuma opção vem marcada).';
    if (o.escolha === 'sem' && o.justificativaSem.trim().length < 4) return 'Informe a justificativa de não haver valor a reembolsar.';
    if (o.escolha === 'ja_reembolsado' && !o.jr.modo) return 'Vincule o pagamento do reembolso já registrado ou registre o pagamento histórico.';
    if (o.escolha === 'ja_reembolsado' && o.jr.modo === 'vinculo' && !o.jr.pagamentoId) return 'Escolha o pagamento a vincular.';
    if (o.escolha === 'ja_reembolsado' && o.jr.modo === 'novo' && o.jr.confirmarDuplicidade && o.jr.justificativaDuplicidade.trim().length < 4) return 'Diga por que é outro pagamento (mesma conta, data e valor).';
  }
  return null;
}

export function EscolhaQuemPagou({ o, set, permitidas = ['conta', 'cartao', 'pessoa'] }: { o: Origem; set: (x: Origem) => void; permitidas?: TipoOrigem[] }) {
  const opcoes: [TipoOrigem, string, string][] = [
    ['conta', 'Conta da Inttrix', 'conta bancária PJ · saída de caixa'],
    ['cartao', 'Cartão da Inttrix', 'entra numa fatura · a saída é o pagamento da fatura'],
    ['pessoa', 'Pessoa que pagou', 'pessoa financeira · reembolso conforme a escolha'],
  ];
  return (
    <fieldset className="opcoes-cartao tres origem-recursos" data-teste="origem-recursos">
      <legend>
        Quem pagou{' '}
        <Ajuda titulo="Quem pagou">
          A despesa é registrada uma vez. Só a conta da Inttrix é saída de caixa: no cartão, a saída é o pagamento da fatura; quando uma pessoa pagou, é o pagamento do reembolso (se houver).
        </Ajuda>
      </legend>
      {opcoes.map(([id, nome, desc]) => {
        const ok = permitidas.includes(id);
        return (
          <label key={id} className={`opcao-cartao${o.tipo === id ? ' escolhida' : ''}${ok ? '' : ' desabilitada'}`} title={ok ? undefined : 'Não se aplica a este lançamento'}>
            <input type="radio" name="quem-pagou" checked={o.tipo === id} disabled={!ok} onChange={() => set({ ...o, tipo: id })} data-teste={`origem-${id}`} />
            <span>
              <strong>{nome}</strong>
              <small>{desc}</small>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}

/** Lista de cartões ativos (da aba Cartões). */
function useCartoes() {
  return useCarga(async () => (await api.get<ListaCartoes>('/api/contas-pagar/cartoes')).cartoes.filter((x) => x.ativo), []);
}

/** P1: cartão, fatura sugerida pelo fechamento (corrigível com justificativa). */
export function CamposCartao({
  o,
  set,
  data,
  parcelas = 1,
  valores,
  rotulos,
}: {
  o: Origem;
  set: (x: Origem) => void;
  data: string;
  parcelas?: number;
  valores: number[];
  /** várias obrigações pagas juntas (cada uma na fatura da data); sem rótulos, são parcelas (uma em cada fatura) */
  rotulos?: string[];
}) {
  const cartoes = useCartoes();
  const lista = cartoes.dados ?? [];
  useEffect(() => {
    if (!o.cartaoId && lista[0]) set({ ...o, cartaoId: lista[0].id });
  }, [lista, o, set]);
  const cartao = lista.find((x) => x.id === o.cartaoId);
  const sugerida = cartao && data ? faturaSugerida(data, cartao.diaFechamento, cartao.diaVencimento) : null;
  const opcoesMes = sugerida ? [-1, 0, 1, 2].map((k) => datasDaFatura(somarMes(sugerida.mes, k), cartao!.diaFechamento, cartao!.diaVencimento)) : [];
  const escolhida = o.faturaMes || sugerida?.mes || '';
  const diferente = Boolean(sugerida && o.faturaMes && o.faturaMes !== sugerida.mes);
  return (
    <section className="quadro-form" data-teste="campos-cartao">
      <div className="grade-form duas">
        <Campo rotulo="Cartão da Inttrix">
          {(id) => (
            <select id={id} value={o.cartaoId} onChange={(e) => set({ ...o, cartaoId: e.target.value, faturaMes: '' })} data-teste="cartao-pagamento">
              {lista.length === 0 && <option value="">{cartoes.carregando ? 'Carregando…' : 'Nenhum cartão ativo (Cadastros base)'}</option>}
              {lista.map((x) => (
                <option key={x.id} value={x.id}>
                  {nomeCartao(x)}
                </option>
              ))}
            </select>
          )}
        </Campo>
      </div>
      {sugerida && (
        <table className="tabela tabela-compacta" data-teste="faturas-sugeridas">
          <thead>
            <tr>
              <th>{rotulos ? 'Obrigação' : 'Parcela'}</th>
              <th className="numero">Valor</th>
              <th>Fatura</th>
              <th>Situação</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: parcelas }, (_, i) => {
              const f = i === 0 ? datasDaFatura(escolhida, cartao!.diaFechamento, cartao!.diaVencimento) : faturaSugerida(data, cartao!.diaFechamento, cartao!.diaVencimento, rotulos ? 0 : i);
              return (
                <tr key={i}>
                  <td>{rotulos?.[i] ?? (parcelas === 1 ? 'única' : `${i + 1}/${parcelas}`)}</td>
                  <td className="numero">{rs((valores[i] ?? 0) / 100)}</td>
                  <td>
                    {i === 0 && parcelas === 1 ? (
                      <select
                        value={escolhida}
                        onChange={(e) =>
                          set({
                            ...o,
                            faturaMes: e.target.value === sugerida.mes ? '' : e.target.value,
                          })
                        }
                        aria-label="Fatura"
                        data-teste="fatura-escolhida"
                      >
                        {opcoesMes.map((x) => (
                          <option key={x.mes} value={x.mes}>
                            {mesCurto(x.mes)} · fecha {dataBr(x.fechamento).slice(0, 5)} · vence {dataBr(x.vencimento).slice(0, 5)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="sem-quebra">
                        {mesCurto(f.mes)} · fecha {dataBr(f.fechamento).slice(0, 5)} · vence {dataBr(f.vencimento).slice(0, 5)}
                      </span>
                    )}
                  </td>
                  <td>{i === 0 && diferente ? <SeloSituacao texto="Escolhida" tom="parcial" /> : <span className="selo-projetado">Sugerida</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {diferente && (
        <Campo rotulo="Justificativa da fatura escolhida">
          {(id) => <input id={id} value={o.justificativaFatura} onChange={(e) => set({ ...o, justificativaFatura: e.target.value })} maxLength={500} data-teste="justificativa-fatura" />}
        </Campo>
      )}
      {sugerida && (
        <p className="texto-secundario sem-margem">
          {Number(data.slice(8, 10)) > cartao!.diaFechamento ? `Compra depois do fechamento (dia ${cartao!.diaFechamento}) entra na fatura seguinte. ` : ''}
          {rotulos ? 'As obrigações ficam quitadas pelo cartão (uma cobrança para cada)' : 'A obrigação fica quitada pelo cartão'}; a dívida passa para a fatura
          {parcelas > 1 && !rotulos ? ' (uma parcela em cada fatura)' : ''}. A fatura sugerida fica “a conferir” até a conferência com o emissor, onde se corrige.
        </p>
      )}
    </section>
  );
}

/** Busca de pessoa financeira (sem expor CPF: o servidor já mascara conforme o perfil). */
function BuscaPessoa({ valor, aoEscolher }: { valor: { id: string; nome: string } | null; aoEscolher: (p: { id: string; nome: string } | null) => void }) {
  const [termo, setTermo] = useState('');
  const [itens, setItens] = useState<{ id: string; nome: string; documento: string; tipo: string }[]>([]);
  useEffect(() => {
    if (termo.trim().length < 2) return setItens([]);
    const t = setTimeout(() => {
      void api
        .get<{ id: string; nome: string; documento: string; tipo: string }[]>(`/api/contas-pagar/favorecidos?busca=${encodeURIComponent(termo.trim())}`)
        .then((r) => setItens(r.filter((x) => x.tipo === 'pessoa')))
        .catch(() => setItens([]));
    }, 250);
    return () => clearTimeout(t);
  }, [termo]);
  if (valor)
    return (
      <div className="favorecido-escolhido" data-teste="pessoa-escolhida">
        <strong>{valor.nome}</strong>
        <button type="button" className="botao-link" onClick={() => aoEscolher(null)}>
          Trocar
        </button>
      </div>
    );
  return (
    <div className="busca-favorecido">
      <input type="search" value={termo} onChange={(e) => setTermo(e.target.value)} placeholder="Nome da pessoa (2 letras ou mais)" aria-label="Pessoa que pagou" data-teste="busca-pessoa" />
      {itens.length > 0 && (
        <ul className="opcoes-favorecido" role="listbox" aria-label="Pessoas encontradas">
          {itens.map((x) => (
            <li key={x.id}>
              <button type="button" role="option" aria-selected={false} onClick={() => (aoEscolher({ id: x.id, nome: x.nome }), setTermo(''))} data-teste={`pessoa-${x.nome}`}>
                <strong>{x.nome}</strong>
                <small>{x.documento}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** P2: pessoa que pagou — três escolhas sem pré-marcação; "Já reembolsado" vincula ou registra sem duplicar a saída. */
export function CamposPessoa({ o, set, valorPago, bancos, erroApi }: { o: Origem; set: (x: Origem) => void; valorPago: string; bancos: ContaBancaria[]; erroApi: ErroApi | null }) {
  const jr = o.jr;
  const setJr = (x: Partial<Origem['jr']>) => set({ ...o, jr: { ...jr, ...x } });
  const disp = useCarga(async () => (o.pessoa && o.escolha === 'ja_reembolsado' ? api.get<Disponivel[]>(`/api/contas-pagar/reembolsos/pagamentos-disponiveis?pessoa_id=${o.pessoa.id}`) : []), [o.pessoa?.id, o.escolha]);
  useEffect(() => {
    if (!jr.contaId && bancos[0]) set({ ...o, jr: { ...jr, contaId: bancos[0].id } });
  }, [bancos, jr, o, set]);
  const valorC = centavosDe(valorPago) || 0;
  const existente =
    erroApi?.codigo === 'transacao_ja_registrada'
      ? (
          erroApi.detalhes as {
            pagamento?: {
              id: string;
              numero: string;
              disponivel: string;
              podeVincular?: boolean;
            };
          }
        )?.pagamento
      : null;
  const escolhas: [EscolhaReembolso, string, string][] = [
    ['gera', 'Gera reembolso', `a Inttrix passa a dever ${valorC ? rs(valorC / 100) : 'o valor'} à pessoa`],
    ['ja_reembolsado', 'Já reembolsado', 'vincular ou registrar o pagamento do reembolso, sem duplicar a saída'],
    ['sem', 'Não há valor a reembolsar', 'exige justificativa · nada passa a ser devido'],
  ];
  return (
    <section className="quadro-form" data-teste="campos-pessoa">
      <div className="campo">
        <span className="rotulo-campo">
          Pessoa que pagou <Ajuda titulo="Pessoa que pagou">Pessoas financeiras (com ou sem usuário no HUB). Cadastre em Administração › Cadastros base.</Ajuda>
        </span>
        <BuscaPessoa valor={o.pessoa} aoEscolher={(p) => set({ ...o, pessoa: p, jr: { ...jr, pagamentoId: '' } })} />
      </div>
      <fieldset className="opcoes-cartao tres" data-teste="escolha-reembolso">
        <legend>
          Reembolso (escolha obrigatória) <Ajuda titulo="Reembolso">Nenhuma opção vem marcada: pagamentos pessoais históricos nunca são presumidos devidos.</Ajuda>
        </legend>
        {escolhas.map(([id, nome, desc]) => (
          <label key={id} className={`opcao-cartao${o.escolha === id ? ' escolhida' : ''}`}>
            <input type="radio" name="escolha-reembolso" checked={o.escolha === id} onChange={() => set({ ...o, escolha: id })} data-teste={`reembolso-${id}`} />
            <span>
              <strong>{nome}</strong>
              <small>{desc}</small>
            </span>
          </label>
        ))}
      </fieldset>
      {o.escolha === 'sem' && (
        <Campo rotulo="Justificativa (não há valor a reembolsar)">
          {(id) => (
            <input
              id={id}
              value={o.justificativaSem}
              onChange={(e) => set({ ...o, justificativaSem: e.target.value })}
              maxLength={500}
              placeholder="Ex.: acertado no fechamento de agosto"
              data-teste="justificativa-sem-reembolso"
            />
          )}
        </Campo>
      )}
      {o.escolha === 'ja_reembolsado' && (
        <div className="quadro-form subquadro" data-teste="ja-reembolsado">
          <h3>Pagamento do reembolso</h3>
          <div className="linha-opcoes">
            <label className="caixa-marcar">
              <input type="radio" name="modo-jr" checked={jr.modo === 'vinculo'} onChange={() => setJr({ modo: 'vinculo' })} data-teste="jr-vinculo" />
              <span>Vincular pagamento já registrado</span>
            </label>
            <label className="caixa-marcar">
              <input type="radio" name="modo-jr" checked={jr.modo === 'novo'} onChange={() => setJr({ modo: 'novo', valor: jr.valor || valorPago })} data-teste="jr-novo" />
              <span>Registrar o pagamento histórico</span>
            </label>
          </div>
          {jr.modo === 'vinculo' &&
            (!o.pessoa ? (
              <p className="texto-secundario">Escolha a pessoa para ver os pagamentos feitos a ela.</p>
            ) : (disp.dados ?? []).length === 0 ? (
              <p className="texto-secundario" data-teste="sem-disponiveis">
                Nenhum pagamento à pessoa com saldo disponível para vínculo. Registre o pagamento histórico.
              </p>
            ) : (
              <div className="rolagem">
                <table className="tabela tabela-compacta" data-teste="disponiveis">
                  <thead>
                    <tr>
                      <th />
                      <th>Pagamento</th>
                      <th>Conta</th>
                      <th className="numero">Valor</th>
                      <th className="numero">Disponível</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(disp.dados ?? []).map((x) => {
                      const cobre = c(x.disponivel) >= valorC;
                      return (
                        <tr key={x.id} className={cobre ? undefined : 'linha-estornada'} title={x.identificador ?? undefined}>
                          <td>
                            <input
                              type="radio"
                              name="vinculo"
                              checked={jr.pagamentoId === x.id}
                              disabled={!cobre}
                              onChange={() => setJr({ pagamentoId: x.id })}
                              aria-label={`Vincular ${x.numero}`}
                              data-teste={`vincular-${x.numero}`}
                            />
                          </td>
                          <td>
                            {x.numero}
                            <small className="bloco texto-secundario sem-quebra">
                              {dataBr(x.data)} · {MEIOS[x.meio] ?? x.meio}
                            </small>
                          </td>
                          <td className="sem-quebra">{x.conta.split(' · ').slice(-1)[0]}</td>
                          <td className="numero">{rs(x.valor)}</td>
                          <td className="numero">
                            <strong>{rs(x.disponivel)}</strong>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          {jr.modo === 'novo' && (
            <div className="grade-form quatro">
              <CampoData rotulo="Data" valor={jr.data} aoMudar={(d) => setJr({ data: d })} max={hojeIso()} obrigatorio teste="jr-data" />
              <Campo rotulo="Conta da Inttrix">
                {(id) => (
                  <select id={id} value={jr.contaId} onChange={(e) => setJr({ contaId: e.target.value })} data-teste="jr-conta">
                    {bancos.map((b) => (
                      <option key={b.id} value={b.id}>
                        {nomeBanco(b)}
                      </option>
                    ))}
                  </select>
                )}
              </Campo>
              <Campo rotulo="Meio">
                {(id) => (
                  <select id={id} value={jr.meio} onChange={(e) => setJr({ meio: e.target.value })} data-teste="jr-meio">
                    {Object.entries(MEIOS).map(([k, n]) => (
                      <option key={k} value={k}>
                        {n}
                      </option>
                    ))}
                  </select>
                )}
              </Campo>
              <Campo
                rotulo="Valor pago à pessoa"
                dica={<Ajuda titulo="Valor pago à pessoa">Se um único pagamento cobriu várias despesas, informe o valor total: o que sobrar fica disponível para vincular às próximas, sem duplicar a saída.</Ajuda>}
              >
                {(id) => <input id={id} value={jr.valor} onChange={(e) => setJr({ valor: e.target.value })} inputMode="decimal" data-teste="jr-valor" />}
              </Campo>
              <div className="largura-total">
                <Campo rotulo="Identificador da transação (opcional)">
                  {(id) => <input id={id} value={jr.identificador} onChange={(e) => setJr({ identificador: e.target.value })} maxLength={120} data-teste="jr-identificador" />}
                </Campo>
              </div>
            </div>
          )}
          {existente && (
            <div className="aviso aviso-ok-fin" role="status" data-teste="aviso-existente">
              Este identificador já existe na conta: <strong>{existente.numero}</strong>
              {existente.podeVincular ? (
                <>
                  {' '}
                  (disponível {rs(existente.disponivel)}). Vincule esse pagamento em vez de registrar outra saída.{' '}
                  <button type="button" className="botao-link" onClick={() => setJr({ modo: 'vinculo', pagamentoId: existente.id })} data-teste="usar-existente">
                    Vincular {existente.numero}
                  </button>
                </>
              ) : (
                ' — confira se não é o mesmo pagamento.'
              )}
            </div>
          )}
          {erroApi?.codigo === 'possivel_duplicidade' && jr.modo === 'novo' && (
            <div className="aviso aviso-atencao-fin" role="status" data-teste="possivel-duplicidade">
              <strong>Possível duplicidade — confira.</strong> Na mesma conta, data e valor já existe:
              <ul>
                {((erroApi.detalhes as { pagamentos?: { id: string; numero: string; identificador: string | null; pessoa: string | null; disponivel: string; podeVincular: boolean }[] })?.pagamentos ?? []).map((x) => (
                  <li key={x.id}>
                    {x.numero}
                    {x.identificador ? ` · ${x.identificador}` : ''}
                    {x.pessoa ? ` · pago a ${x.pessoa} · disponível ${rs(x.disponivel)}` : ''}{' '}
                    {x.podeVincular && (
                      <button type="button" className="botao-link" onClick={() => setJr({ modo: 'vinculo', pagamentoId: x.id })} data-teste={`vincular-coincidente-${x.numero}`}>
                        É o mesmo: vincular {x.numero}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              <label className="caixa-marcar" data-teste="confirmar-duplicidade">
                <input type="checkbox" checked={jr.confirmarDuplicidade} onChange={(e) => setJr({ confirmarDuplicidade: e.target.checked })} />
                <span>É outro pagamento legítimo (coincide em conta, data e valor)</span>
              </label>
              {jr.confirmarDuplicidade && (
                <Campo rotulo="Por que é outro pagamento">
                  {(id) => (
                    <input
                      id={id}
                      value={jr.justificativaDuplicidade}
                      onChange={(e) => setJr({ justificativaDuplicidade: e.target.value })}
                      maxLength={500}
                      placeholder="Ex.: dois reembolsos no mesmo dia (ida e volta)"
                      data-teste="justificativa-duplicidade"
                    />
                  )}
                </Campo>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/* ====================================================================== aba Cartões (C1) */
export function AbaCartoes({ abas }: { abas: ReactNode }) {
  const { pode } = useSessao();
  const [cartaoId, setCartaoId] = useState('');
  const [situacao, setSituacao] = useState('');
  const carga = useCarga(() => api.get<ListaCartoes>(`/api/contas-pagar/cartoes${cartaoId ? `?cartao_id=${cartaoId}` : ''}`), [cartaoId]);
  // vindo da ficha da despesa ("recuperado na FAT-…"): abre a ficha da fatura pedida
  const [aberta, setAberta] = useState<string | null>(() => {
    try {
      const n = sessionStorage.getItem('inttrix:contas-pagar:abrir-fatura');
      sessionStorage.removeItem('inttrix:contas-pagar:abrir-fatura');
      return n;
    } catch {
      return null;
    }
  });
  const [nova, setNova] = useState(false);
  const [lancar, setLancar] = useState(false);
  const d = carga.dados;
  const lista = (d?.faturas ?? []).filter((f) => !situacao || (situacao === 'em_aberto' ? c(f.saldo) > 0 : f.situacao === situacao));
  const r = d?.resumo;
  return (
    <>
      <h1 className="titulo-pagina rotulo-oculto">Contas a Pagar · Cartões</h1>
      {r && (
        <CardsLista n={4} teste="cards-cartoes">
          <CardLista
            icone="alerta"
            tom="vermelho"
            rotulo="Faturas em aberto"
            valor={rs(r.emAberto.valor)}
            detalhe={`${plural(r.emAberto.quantidade, 'fatura', 'faturas')} · saldo a pagar`}
            ajuda={<Ajuda titulo="Faturas em aberto">Saldo das faturas (dívida com o emissor). O saldo levado para a fatura seguinte continua na original e não é somado de novo.</Ajuda>}
            teste="card-faturas-aberto"
          />
          <CardLista
            icone="relogio"
            tom="roxo"
            rotulo="Vencem em 7 dias"
            valor={rs(r.vencem7.valor)}
            detalhe={r.vencem7.proxima ? `fatura ${mesCurto(r.vencem7.proxima.mes)} · ${dataBr(r.vencem7.proxima.vencimento)}` : 'nenhuma fatura'}
            teste="card-faturas-7dias"
          />
          <CardLista
            icone="documento"
            tom="azul"
            rotulo="A conferir"
            valor={plural(r.aConferir.quantidade, 'compra', 'compras')}
            detalhe="fatura sugerida pelo fechamento"
            ajuda={<Ajuda titulo="A conferir">Compras na fatura sugerida pelo fechamento do cartão, ainda não conferidas com a fatura do emissor.</Ajuda>}
            teste="card-a-conferir"
          />
          <CardLista icone="dinheiro" tom="verde" rotulo="Pago em faturas no mês" valor={rs(r.pagoNoMes.valor)} detalhe="saída da conta da Inttrix" teste="card-pago-faturas" />
        </CardsLista>
      )}
      <div className="linha-abas-acao">{abas}</div>
      <div className="barra-filtros-fin" data-teste="filtros-cartoes">
        <label className="filtro-fixo">
          Cartão
          <select value={cartaoId} onChange={(e) => setCartaoId(e.target.value)} data-teste="filtro-cartao">
            <option value="">Todos os cartões</option>
            {(d?.cartoes ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {nomeCartao(x)}
                {x.ativo ? '' : ' (inativo)'}
              </option>
            ))}
          </select>
        </label>
        <label className="filtro-fixo">
          Situação
          <select value={situacao} onChange={(e) => setSituacao(e.target.value)} data-teste="filtro-situacao-fatura">
            <option value="">Todas</option>
            <option value="em_aberto">Com saldo</option>
            <option value="aberta">Aberta</option>
            <option value="fechada">Fechada</option>
            <option value="parcial">Parcial</option>
            <option value="paga">Paga</option>
          </select>
        </label>
        <span className="espaco" />
        {pode('lancar', 'conta_pagar') && (
          <button type="button" className="botao-secundario" onClick={() => setLancar(true)} disabled={!d?.faturas.length} data-teste="informar-encargo">
            Informar encargo ou crédito
          </button>
        )}
        {pode('lancar', 'conta_pagar') && pode('registrar', 'pagamento') && (
          <button type="button" className="botao-primario" onClick={() => setNova(true)} data-teste="nova-compra-cartao">
            Nova compra no cartão
          </button>
        )}
      </div>
      {!d ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="cartoes" />
      ) : lista.length === 0 ? (
        <p className="vazio" data-teste="cartoes-vazio">
          {d.cartoes.length === 0 ? 'Nenhum cartão da Inttrix cadastrado (Administração › Cadastros base).' : 'Nenhuma fatura com os filtros atuais. Use “Nova compra no cartão” para lançar.'}
        </p>
      ) : (
        <RolagemIndicada className="rolagem quadro-tabela" teste="faturas">
          <table className="tabela tabela-faturas" data-teste="lista-faturas">
            <thead>
              <tr>
                <th>Fatura</th>
                <th>Cartão</th>
                <th>Fechamento</th>
                <th>Vencimento</th>
                <th className="numero">Cobrado</th>
                <th className="numero">Pago</th>
                <th className="numero">Saldo</th>
                <th>Situação</th>
                <th>Conferência</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((f) => {
                const s = SITUACAO_FATURA[f.situacao];
                // composição do cobrado: uma linha curta; com mais de uma parte, área expansível (valores nunca quebram)
                const partes = [
                  `compras ${rs(f.compras)}`,
                  c(f.encargos) ? `encargos ${rs(f.encargos)}` : '',
                  c(f.creditos) ? `créditos − ${rs(f.creditos)}` : '',
                  c(f.saldoAnteriorVinculo) ? `saldo anterior ${rs(f.saldoAnteriorVinculo)} (vínculo)` : '',
                ].filter(Boolean);
                return (
                  <tr key={f.id} data-teste={`fatura-${f.numero}`}>
                    <td>
                      <button type="button" className="botao-link link-forte" onClick={() => setAberta(f.numero)} data-teste={`abrir-${f.numero}`}>
                        {f.numero}
                      </button>
                      <small className="bloco texto-secundario">{mesCurto(f.mes)}</small>
                    </td>
                    <td className="sem-quebra">final {f.cartao.final}</td>
                    <td className="sem-quebra">{dataBr(f.fechamento)}</td>
                    <td className="sem-quebra">{dataBr(f.vencimento)}</td>
                    <td className="numero col-cobrado">
                      {rs(f.cobrado)}
                      {partes.length <= 1 ? (
                        <small className="bloco texto-secundario sem-quebra">{partes[0]}</small>
                      ) : (
                        <details className="composicao-fatura" data-teste={`composicao-${f.numero}`}>
                          <summary className="texto-secundario">composição ({partes.length})</summary>
                          {partes.map((x) => (
                            <small key={x} className="bloco texto-secundario sem-quebra">
                              {x}
                            </small>
                          ))}
                        </details>
                      )}
                    </td>
                    <td className="numero">{rs(f.pago)}</td>
                    <td className="numero">
                      {rs(f.saldo)}
                      {c(f.levadoAdiante) > 0 && c(f.saldo) > 0 && <small className="bloco texto-secundario">levado adiante {rs(f.levadoAdiante)}</small>}
                    </td>
                    <td>
                      <span className="celula-situacao-pagar">
                        <SeloSituacao texto={s.texto} tom={s.tom} />
                        {f.vencida && <small className="texto-alerta">vencida</small>}
                      </span>
                    </td>
                    <td>
                      {f.conferencia ? (
                        c(f.conferencia.diferenca) === 0 ? (
                          <span className="chip-fin">conferida</span>
                        ) : (
                          <span className="chip-fin alerta" title={`Emissor ${rs(f.conferencia.totalEmissor)} × HUB ${rs(f.conferencia.totalRegistrado)}`}>
                            diferença {rs(Math.abs(c(f.conferencia.diferenca)) / 100)}
                          </span>
                        )
                      ) : f.aConferir > 0 ? (
                        <span className="chip-fin">{plural(f.aConferir, 'sugerida', 'sugeridas')}</span>
                      ) : (
                        <span className="texto-secundario">{f.situacao === 'aberta' ? 'a fechar' : '—'}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </RolagemIndicada>
      )}
      <p className="texto-secundario nota-fin">
        A fatura é a dívida com o emissor: as compras já estão nas despesas (uma vez); aqui só entram encargos e créditos. O saldo levado adiante continua devido na fatura original e aparece na seguinte por vínculo, sem
        somar duas vezes.
      </p>
      {aberta && <FichaFaturaModal numero={aberta} aoFechar={() => (setAberta(null), void carga.recarregar())} />}
      {nova && <NovaDespesa compraCartao aoFechar={() => setNova(false)} aoSalvar={() => (setNova(false), void carga.recarregar())} />}
      {lancar && d && <InformarLancamento faturas={d.faturas} aoFechar={() => setLancar(false)} aoSalvar={() => (setLancar(false), void carga.recarregar())} />}
    </>
  );
}

/* ---------- ficha da fatura (abre a conferência, lançamentos, pagamento e transporte) */
function FichaFaturaModal({ numero, aoFechar }: { numero: string; aoFechar: () => void }) {
  const { pode } = useSessao();
  const carga = useCarga(() => api.get<FichaFatura>(`/api/contas-pagar/cartoes/faturas/${encodeURIComponent(numero)}`), [numero]);
  const [acao, setAcao] = useState<'' | 'conferir' | 'lancar' | 'pagar' | 'transportar'>('');
  const [anular, setAnular] = useState<FichaFatura['lancamentos'][number] | null>(null);
  const [desfazer, setDesfazer] = useState<FichaFatura['transportes'][number] | null>(null);
  const [avisos, setAvisos] = useState<string[]>([]);
  const f = carga.dados;
  const voltar = () => (setAcao(''), setAnular(null), setDesfazer(null), void carga.recarregar());
  return (
    <Modal
      titulo={`Fatura ${numero}${f ? ` · ${mesCurto(f.fatura.mes)} · ${nomeCartao(f.fatura.cartao)}` : ''}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Fechar
          </button>
          {f && pode('editar', 'conta_pagar') && c(f.fatura.saldo) > 0 && (
            <button type="button" className="botao-secundario" onClick={() => setAcao('transportar')} data-teste="levar-saldo">
              Levar saldo adiante
            </button>
          )}
          {f && pode('editar', 'conta_pagar') && (
            <button type="button" className="botao-secundario" onClick={() => setAcao('conferir')} data-teste="conferir-fatura">
              Conferir com o emissor
            </button>
          )}
          {f && pode('lancar', 'conta_pagar') && (
            <button type="button" className="botao-secundario" onClick={() => setAcao('lancar')} data-teste="lancar-fatura">
              Informar encargo ou crédito
            </button>
          )}
          {f && pode('registrar', 'pagamento') && f.faturasComSaldo.length > 0 && (
            <button type="button" className="botao-primario" onClick={() => setAcao('pagar')} data-teste="pagar-fatura">
              Registrar pagamento
            </button>
          )}
        </>
      }
    >
      {!f ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} />
      ) : (
        <div className="ficha-fatura" data-teste="ficha-fatura">
          <p className="faixa-resumo-modal">
            <span>
              Fecha <strong>{dataBr(f.fatura.fechamento)}</strong> · vence <strong>{dataBr(f.fatura.vencimento)}</strong>
            </span>
            <span>
              Cobrado <strong>{rs(f.fatura.cobrado)}</strong>
            </span>
            <span>
              Pago <strong>{rs(f.fatura.pago)}</strong>
            </span>
            <span>
              Saldo <strong>{rs(f.fatura.saldo)}</strong>
            </span>
            <SeloSituacao texto={SITUACAO_FATURA[f.fatura.situacao].texto} tom={SITUACAO_FATURA[f.fatura.situacao].tom} />
          </p>
          {f.periodoFechado && <p className="aviso aviso-atencao-fin">Período financeiro do fechamento está fechado: lançamentos nesta fatura ficam bloqueados.</p>}
          {avisos.map((x) => (
            <p key={x} className="aviso aviso-atencao-fin" role="status" data-teste="aviso-desfazer">
              {x}
            </p>
          ))}
          <section className="quadro-form">
            <h3>Compras ({f.compras.length})</h3>
            {f.compras.length === 0 ? (
              <p className="texto-secundario sem-margem">Nenhuma compra nesta fatura.</p>
            ) : (
              <div className="rolagem">
                <table className="tabela tabela-compacta">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Compra</th>
                      <th>Parcela</th>
                      <th className="numero">Valor</th>
                      <th>Fatura</th>
                    </tr>
                  </thead>
                  <tbody>
                    {f.compras.map((x) => (
                      <tr key={x.pagamentoId}>
                        <td className="sem-quebra">{dataBr(x.data)}</td>
                        <td>
                          <a href={`#/contas-pagar/${x.obrigacao}`} className="link-forte">
                            {x.descricao}
                          </a>
                          <small className="bloco texto-secundario">
                            {x.obrigacao} · {x.favorecido}
                          </small>
                        </td>
                        <td>{x.parcela ?? '—'}</td>
                        <td className="numero">{rs(x.valor)}</td>
                        <td>{x.situacao === 'conferida' ? <span className="chip-fin">conferida</span> : <span className="selo-projetado">Sugerida · a conferir</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          {(f.lancamentos.length > 0 || f.transportes.length > 0) && (
            <section className="quadro-form">
              <h3>Encargos, créditos e vínculos</h3>
              <div className="rolagem">
                <table className="tabela tabela-compacta">
                  <tbody>
                    {f.lancamentos.map((l) => (
                      <tr key={l.id} className={l.anulacao ? 'linha-estornada' : undefined}>
                        <td>{l.tipo === 'encargo' ? 'Encargo' : l.tipo === 'credito' ? (l.categoria === 'cashback' ? 'Cashback' : 'Crédito do emissor') : 'Devolução de compra'}</td>
                        <td>
                          {l.descricao}
                          <small className="bloco texto-secundario">{l.compraOrigem ? `${l.compraOrigem.obrigacao} · ${l.compraOrigem.descricao}` : l.conta}</small>
                          {l.anulacao && <small className="bloco texto-secundario">anulado: {l.anulacao.justificativa}</small>}
                        </td>
                        <td className="numero">
                          {l.tipo === 'encargo' ? '' : '− '}
                          {rs(l.valor)}
                        </td>
                        <td>
                          {!l.anulacao && pode('estornar', 'pagamento') && (
                            <button type="button" className="botao-link" onClick={() => setAnular(l)} data-teste={`anular-${l.id}`}>
                              Anular
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {f.transportes.map((t) => (
                      <tr key={t.id} className={t.desfeito ? 'linha-estornada' : undefined} data-teste={`transporte-${t.origem.numero}-${t.destino.numero}`}>
                        <td>{t.sentido === 'saida' ? 'Saldo levado adiante' : 'Saldo anterior (vínculo)'}</td>
                        <td>
                          {t.sentido === 'saida' ? `para ${t.destino.numero} · ${mesCurto(t.destino.mes)}` : `de ${t.origem.numero} · ${mesCurto(t.origem.mes)} (a dívida continua lá)`}
                          <small className="bloco texto-secundario">{t.justificativa}</small>
                          {t.desfeito && <small className="bloco texto-secundario">desfeito: {t.desfeito.justificativa}</small>}
                        </td>
                        <td className="numero">{rs(t.valor)}</td>
                        <td>
                          {!t.desfeito && pode('editar', 'conta_pagar') && (
                            <button type="button" className="botao-link" onClick={() => setDesfazer(t)} data-teste={`desfazer-transporte-${t.id}`}>
                              Desfazer
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
          {f.pagamentos.length > 0 && (
            <section className="quadro-form">
              <h3>Pagamentos da fatura</h3>
              <table className="tabela tabela-compacta">
                <tbody>
                  {f.pagamentos.map((p) => (
                    <tr key={p.id} className={p.estorno ? 'linha-estornada' : undefined}>
                      <td>{p.numero}</td>
                      <td className="sem-quebra">{dataBr(p.data)}</td>
                      <td>
                        {p.conta}
                        <small className="bloco texto-secundario">{MEIOS[p.meio] ?? p.meio}</small>
                      </td>
                      <td className="numero">
                        {rs(p.aplicado)}
                        {c(p.aplicado) !== c(p.valor) && <small className="bloco texto-secundario">de {rs(p.valor)}</small>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
          {f.conferencias.length > 0 && (
            <p className="texto-secundario sem-margem">
              Última conferência: emissor {rs(f.conferencias.at(-1)!.totalEmissor)} × HUB {rs(f.conferencias.at(-1)!.totalRegistrado)} · diferença {rs(f.conferencias.at(-1)!.diferenca)} (
              {f.conferencias.at(-1)!.autor ?? '—'})
            </p>
          )}
        </div>
      )}
      {f && acao === 'conferir' && <ConferirFatura ficha={f} aoFechar={() => setAcao('')} aoSalvar={voltar} />}
      {f && acao === 'lancar' && <InformarLancamento faturas={[f.fatura]} ficha={f} aoFechar={() => setAcao('')} aoSalvar={voltar} />}
      {f && acao === 'pagar' && <PagarFatura ficha={f} aoFechar={() => setAcao('')} aoSalvar={voltar} />}
      {f && acao === 'transportar' && <TransportarSaldo ficha={f} aoFechar={() => setAcao('')} aoSalvar={voltar} />}
      {anular && <AnularLancamento lancamento={anular} aoFechar={() => setAnular(null)} aoSalvar={voltar} />}
      {desfazer && (
        <DesfazerComJustificativa
          titulo="Desfazer transporte de saldo"
          url={`/api/contas-pagar/cartoes/transportes/${desfazer.id}/desfazer`}
          rotulo="Desfazer transporte"
          teste="transporte"
          aoFechar={() => setDesfazer(null)}
          aoSalvar={(r) => (setAvisos((r as { avisos?: string[] }).avisos ?? []), voltar())}
        >
          <p>
            Transporte de {rs(desfazer.valor)} de {desfazer.origem.numero} ({mesCurto(desfazer.origem.mes)}) para {desfazer.destino.numero} ({mesCurto(desfazer.destino.mes)}).
          </p>
          <ul className="lista-efeitos">
            <li>O transporte original fica no histórico, marcado como desfeito (nada é apagado).</li>
            <li>A dívida continua em {desfazer.origem.numero}: nenhum pagamento muda.</li>
            <li>{desfazer.destino.numero} deixa de mostrar o saldo anterior por vínculo.</li>
          </ul>
        </DesfazerComJustificativa>
      )}
    </Modal>
  );
}

/* ---------- C2: conferir com a fatura do emissor (diferença; nada criado; mover compra) */
function ConferirFatura({ ficha: f, aoFechar, aoSalvar }: { ficha: FichaFatura; aoFechar: () => void; aoSalvar: () => void }) {
  const [emissor, setEmissor] = useState('');
  const [mover, setMover] = useState<Record<string, string>>({});
  const [justificativa, setJustificativa] = useState('');
  const [observacao, setObservacao] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const registrado = c(f.fatura.cobrado);
  const emissorC = centavosDe(emissor);
  const dif = Number.isFinite(emissorC) ? emissorC - registrado : NaN;
  const meses = [-1, 0, 1, 2].map((k) => somarMes(f.fatura.mes, k));
  const movidas = Object.entries(mover).filter(([, m]) => m && m !== f.fatura.mes);
  const salvar = async () => {
    setErro('');
    setSalvando(true);
    try {
      for (const [pid, mes] of movidas)
        await api.post(`/api/contas-pagar/cartoes/cobrancas/${pid}/mover`, {
          fatura_mes: mes,
          justificativa,
        });
      await api.post(`/api/contas-pagar/cartoes/faturas/${f.fatura.numero}/conferir`, {
        total_emissor: emissor,
        observacao: observacao || null,
        conferir_compras: f.compras.filter((x) => !movidas.some(([pid]) => pid === x.pagamentoId)).map((x) => x.pagamentoId),
      });
      aoSalvar();
    } catch (e) {
      setErro(erroDe(e));
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo={`Conferir com a fatura do emissor — ${mesCurto(f.fatura.mes)}`}
      aoFechar={aoFechar}
      largo
      sobreposto
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button
            type="button"
            className="botao-primario"
            disabled={salvando || !Number.isFinite(dif) || (movidas.length > 0 && justificativa.trim().length < 4)}
            onClick={() => void salvar()}
            data-teste="confirmar-conferencia"
          >
            {Number.isFinite(dif) && dif !== 0 ? 'Confirmar conferência (diferença registrada)' : 'Confirmar conferência'}
          </button>
        </>
      }
    >
      <div className="form-pagamento" data-teste="form-conferencia">
        <p className="faixa-resumo-modal">
          <span>
            {f.fatura.numero} · {nomeCartao(f.fatura.cartao)}
          </span>
          <span>
            Fecha {dataBr(f.fatura.fechamento)} · vence {dataBr(f.fatura.vencimento)}
          </span>
        </p>
        <div className="grade-form tres">
          <Campo rotulo="Total cobrado pelo emissor">{(id) => <input id={id} value={emissor} onChange={(e) => setEmissor(e.target.value)} inputMode="decimal" placeholder="0,00" data-teste="total-emissor" />}</Campo>
          <Campo rotulo="Registrado no HUB">{(id) => <input id={id} value={deCentavos(registrado)} readOnly className="somente-leitura" />}</Campo>
          <Campo rotulo="Diferença">
            {(id) => (
              <input
                id={id}
                readOnly
                className={`somente-leitura${Number.isFinite(dif) && dif !== 0 ? ' texto-diferenca' : ''}`}
                value={!Number.isFinite(dif) ? '—' : dif === 0 ? 'confere' : `${rs(Math.abs(dif) / 100)} a ${dif > 0 ? 'mais no emissor' : 'menos no emissor'}`}
                data-teste="diferenca"
              />
            )}
          </Campo>
        </div>
        {Number.isFinite(dif) && dif !== 0 && (
          <div className="aviso aviso-atencao-fin" role="status" data-teste="aviso-diferenca">
            <strong>Diferença de {rs(Math.abs(dif) / 100)}.</strong> O HUB não cria despesa, encargo nem ajuste para fechar o total. Confira abaixo: falta registrar alguma compra, encargo ou crédito, ou alguma compra
            está na fatura errada?
          </div>
        )}
        <div className="rolagem">
          <table className="tabela tabela-compacta" data-teste="compras-conferencia">
            <thead>
              <tr>
                <th>Data</th>
                <th>Compra</th>
                <th>Parcela</th>
                <th className="numero">Valor</th>
                <th>Fatura</th>
                <th>Situação</th>
              </tr>
            </thead>
            <tbody>
              {f.compras.map((x) => (
                <tr key={x.pagamentoId}>
                  <td className="sem-quebra">{dataBr(x.data)}</td>
                  <td>{x.descricao}</td>
                  <td>{x.parcela ?? '—'}</td>
                  <td className="numero">{rs(x.valor)}</td>
                  <td>
                    <select
                      value={mover[x.pagamentoId] ?? f.fatura.mes}
                      onChange={(e) => setMover({ ...mover, [x.pagamentoId]: e.target.value })}
                      aria-label={`Fatura de ${x.descricao}`}
                      data-teste={`mover-${x.pagamento}`}
                    >
                      {meses.map((m) => (
                        <option key={m} value={m}>
                          {mesCurto(m)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {(mover[x.pagamentoId] ?? f.fatura.mes) !== f.fatura.mes ? (
                      <SeloSituacao texto="Mover" tom="parcial" />
                    ) : x.situacao === 'conferida' ? (
                      <span className="chip-fin">conferida</span>
                    ) : (
                      <span className="selo-projetado">Sugerida · a conferir</span>
                    )}
                  </td>
                </tr>
              ))}
              {c(f.fatura.encargos) > 0 && (
                <tr>
                  <td>—</td>
                  <td>Encargos informados</td>
                  <td>—</td>
                  <td className="numero">{rs(f.fatura.encargos)}</td>
                  <td>{mesCurto(f.fatura.mes)}</td>
                  <td>
                    <SeloSituacao texto="Informado" tom="positivo" />
                  </td>
                </tr>
              )}
              {c(f.fatura.creditos) > 0 && (
                <tr>
                  <td>—</td>
                  <td>Créditos e devoluções</td>
                  <td>—</td>
                  <td className="numero">− {rs(f.fatura.creditos)}</td>
                  <td>{mesCurto(f.fatura.mes)}</td>
                  <td>
                    <SeloSituacao texto="Informado" tom="positivo" />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {movidas.length > 0 && (
          <Campo rotulo="Justificativa da mudança de fatura">
            {(id) => <input id={id} value={justificativa} onChange={(e) => setJustificativa(e.target.value)} maxLength={500} placeholder="Ex.: o emissor lançou na fatura seguinte" data-teste="justificativa-mover" />}
          </Campo>
        )}
        <Campo rotulo="Observação da conferência (opcional)">{(id) => <input id={id} value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={500} data-teste="observacao-conferencia" />}</Campo>
        <p className="texto-secundario sem-margem">
          Mover uma compra para outra fatura muda só a liquidação (a despesa e a competência ficam), com justificativa e histórico. Para registrar o que faltar, use “Informar encargo ou crédito” ou “Nova compra no
          cartão”.
        </p>
        {erro && (
          <p className="aviso aviso-erro" role="alert" data-teste="erro-conferencia">
            {erro}
          </p>
        )}
      </div>
    </Modal>
  );
}

/* ---------- C3: encargo, crédito do emissor ou devolução de compra (decisão 1 A) */
function InformarLancamento({ faturas, ficha, aoFechar, aoSalvar }: { faturas: Fatura[]; ficha?: FichaFatura; aoFechar: () => void; aoSalvar: () => void }) {
  const apoio = useApoio();
  const a = apoio.dados;
  const padrao = faturas.find((f) => f.situacao === 'fechada' || f.situacao === 'parcial') ?? faturas[0];
  const [numero, setNumero] = useState(padrao?.numero ?? '');
  const fichaCarga = useCarga(async () => (ficha && ficha.fatura.numero === numero ? ficha : numero ? api.get<FichaFatura>(`/api/contas-pagar/cartoes/faturas/${encodeURIComponent(numero)}`) : null), [numero]);
  const fx = fichaCarga.dados;
  const [tipo, setTipo] = useState<'' | 'encargo' | 'credito' | 'devolucao'>('');
  const [conta, setConta] = useState('');
  // crédito do emissor: cashback (conta FIN-004 sugerida, editável) × outro crédito (sem conta sugerida)
  const [categoria, setCategoria] = useState<'' | 'cashback' | 'outro'>('');
  const [origem, setOrigem] = useState('');
  const [valor, setValor] = useState('');
  const [descricao, setDescricao] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const compra = fx?.comprasComCredito.find((x) => x.obrigacaoId === origem);
  // ao escolher a compra, o valor sugerido é o crédito ainda disponível para recuperar (pode ser reduzido; nunca maior)
  const escolherCompra = (id: string) => {
    setOrigem(id);
    const x = fx?.comprasComCredito.find((y) => y.obrigacaoId === id);
    setValor(x ? deCentavos(c(x.disponivel)) : '');
  };
  const valorC = centavosDe(valor);
  const acimaDoDisponivel = tipo === 'devolucao' && Boolean(compra) && valorC > c(compra!.disponivel);
  const salvar = async () => {
    setErro('');
    setSalvando(true);
    try {
      await api.post(`/api/contas-pagar/cartoes/faturas/${encodeURIComponent(numero)}/lancamentos`, {
        tipo,
        valor,
        descricao,
        plano_contas_id: tipo === 'devolucao' ? undefined : conta,
        categoria: tipo === 'credito' && categoria === 'cashback' ? 'cashback' : undefined,
        obrigacao_origem_id: tipo === 'devolucao' ? origem : undefined,
      });
      aoSalvar();
    } catch (e) {
      setErro(erroDe(e));
    } finally {
      setSalvando(false);
    }
  };
  const opcoes: ['encargo' | 'credito' | 'devolucao', string, string][] = [
    ['encargo', 'Encargo', 'juros, multa, IOF, anuidade ou tarifa · conta gerencial e justificativa'],
    ['credito', 'Crédito do emissor', 'cashback, bonificação ou ajuste · conta gerencial; não mexe em despesa'],
    ['devolucao', 'Devolução ou cancelamento de compra', 'ligado à compra de origem · liquida o crédito a recuperar'],
  ];
  const cobrado = fx ? c(fx.fatura.cobrado) : 0;
  return (
    <Modal
      titulo={`Informar encargo ou crédito${fx ? ` — fatura ${mesCurto(fx.fatura.mes)}` : ''}`}
      aoFechar={aoFechar}
      largo
      sobreposto={Boolean(ficha)}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button
            type="button"
            className="botao-primario"
            disabled={salvando || !tipo || !(valorC > 0) || acimaDoDisponivel || descricao.trim().length < 4 || (tipo === 'devolucao' ? !origem : !conta) || (tipo === 'credito' && !categoria)}
            onClick={() => void salvar()}
            data-teste="confirmar-lancamento"
          >
            {tipo === 'devolucao' ? 'Informar crédito de devolução' : tipo === 'credito' ? 'Informar crédito do emissor' : 'Informar encargo'}
          </button>
        </>
      }
    >
      {!a ? (
        <EstadoCarga carregando={apoio.carregando} erro={apoio.erro} recarregar={apoio.recarregar} />
      ) : (
        <div className="form-pagamento" data-teste="form-lancamento-fatura">
          {!ficha && (
            <Campo rotulo="Fatura">
              {(id) => (
                <select id={id} value={numero} onChange={(e) => (setNumero(e.target.value), setOrigem(''))} data-teste="fatura-lancamento">
                  {faturas.map((f) => (
                    <option key={f.id} value={f.numero}>
                      {f.numero} · {mesCurto(f.mes)} · {nomeCartao(f.cartao)}
                    </option>
                  ))}
                </select>
              )}
            </Campo>
          )}
          <fieldset className="opcoes-cartao tres" data-teste="tipo-lancamento">
            <legend>O que o emissor lançou</legend>
            {opcoes.map(([id, nome, desc]) => (
              <label key={id} className={`opcao-cartao${tipo === id ? ' escolhida' : ''}`}>
                <input type="radio" name="tipo-lancamento" checked={tipo === id} onChange={() => (setTipo(id), setValor(''), setCategoria(''), setConta(''))} data-teste={`lancamento-${id}`} />
                <span>
                  <strong>{nome}</strong>
                  <small>{desc}</small>
                </span>
              </label>
            ))}
          </fieldset>
          {tipo === 'credito' && (
            <div className="linha-opcoes" data-teste="categoria-credito">
              <label className="caixa-marcar">
                <input
                  type="radio"
                  name="categoria-credito"
                  checked={categoria === 'cashback'}
                  onChange={() => (setCategoria('cashback'), setConta(a.contasAtivas.find((x) => x.codigo === 'FIN-004')?.id ?? ''))}
                  data-teste="credito-cashback"
                />
                <span>Cashback</span>
              </label>
              <label className="caixa-marcar">
                <input type="radio" name="categoria-credito" checked={categoria === 'outro'} onChange={() => (setCategoria('outro'), setConta(''))} data-teste="credito-outro" />
                <span>Outro crédito do emissor (bonificação, ajuste…)</span>
              </label>
            </div>
          )}
          {tipo && tipo !== 'devolucao' && (tipo === 'encargo' || categoria) && (
            <div className="grade-form duas">
              <Campo
                rotulo="Conta gerencial"
                dica={
                  tipo === 'credito' ? (
                    <Ajuda titulo="Crédito do emissor">
                      Cashback e outros créditos do emissor não reduzem a despesa original nem são devolução de compra: ficam como crédito na fatura, na conta gerencial escolhida. No cashback, a conta FIN-004 vem
                      sugerida e pode ser trocada; o enquadramento na DRE e o tratamento fiscal ficam para a validação contábil.
                    </Ajuda>
                  ) : undefined
                }
              >
                {(id) => (
                  <select id={id} value={conta} onChange={(e) => setConta(e.target.value)} data-teste="conta-lancamento">
                    <option value="">Escolha…</option>
                    {a.contasAtivas.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.codigo} — {x.nome}
                      </option>
                    ))}
                  </select>
                )}
              </Campo>
              <Campo rotulo={tipo === 'encargo' ? 'Valor do encargo' : 'Valor do crédito na fatura'}>
                {(id) => <input id={id} value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal" placeholder="0,00" data-teste="valor-lancamento" />}
              </Campo>
            </div>
          )}
          {tipo === 'devolucao' &&
            (!fx ? (
              <EstadoCarga carregando={fichaCarga.carregando} erro={fichaCarga.erro} recarregar={fichaCarga.recarregar} />
            ) : fx.comprasComCredito.length === 0 ? (
              <div className="aviso aviso-atencao-fin" data-teste="sem-credito-recuperar">
                <strong>Nenhuma compra deste cartão tem crédito a recuperar.</strong> Registre antes a devolução na própria compra (“Completar documento” com o valor efetivo): a despesa é ajustada uma vez e o crédito a
                recuperar fica registrado. Depois, o crédito da fatura só o liquida.
              </div>
            ) : (
              <>
                <div className="grade-form duas">
                  <Campo rotulo="Compra de origem">
                    {(id) => (
                      <select id={id} value={origem} onChange={(e) => escolherCompra(e.target.value)} data-teste="compra-origem">
                        <option value="">Escolha…</option>
                        {fx.comprasComCredito.map((x) => (
                          <option key={x.obrigacaoId} value={x.obrigacaoId}>
                            {x.descricao} · {x.obrigacao} · crédito {rs(x.disponivel)}
                          </option>
                        ))}
                      </select>
                    )}
                  </Campo>
                  <Campo rotulo="Valor do crédito na fatura">{(id) => <input id={id} value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal" data-teste="valor-lancamento" />}</Campo>
                </div>
                {acimaDoDisponivel && (
                  <p className="aviso aviso-atencao-fin" role="status" data-teste="devolucao-acima">
                    O crédito da fatura não pode passar do crédito ainda disponível para recuperar nesta compra ({rs(compra!.disponivel)}). Para devolução parcial, informe um valor menor.
                  </p>
                )}
                {compra && valorC > 0 && !acimaDoDisponivel && (
                  <table className="tabela tabela-compacta" data-teste="etapas-devolucao">
                    <thead>
                      <tr>
                        <th>Etapa</th>
                        <th className="numero">Antes</th>
                        <th className="numero">Depois</th>
                        <th>Registro</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>1. Devolução na compra (já registrada)</td>
                        <td className="numero">despesa {rs(c(compra.valorEfetivo) / 100 + c(compra.credito) / 100)}</td>
                        <td className="numero">despesa {rs(compra.valorEfetivo)}</td>
                        <td>valor efetivo do documento (ajustado uma vez)</td>
                      </tr>
                      <tr>
                        <td>2. Crédito a recuperar da compra</td>
                        <td className="numero">{rs(compra.disponivel)}</td>
                        <td className="numero">{rs(Math.max(c(compra.disponivel) - valorC, 0) / 100)}</td>
                        <td>
                          <strong>liquidado por este crédito na fatura</strong> (vínculo)
                        </td>
                      </tr>
                      <tr>
                        <td>3. Fatura {mesCurto(fx.fatura.mes)}</td>
                        <td className="numero">cobrado {rs(cobrado / 100)}</td>
                        <td className="numero">cobrado {rs((cobrado - valorC) / 100)}</td>
                        <td>crédito de devolução {rs(valorC / 100)}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
                <div className="aviso aviso-ok-fin">
                  A despesa já foi ajustada na devolução: este crédito <strong>não reduz a despesa de novo</strong>, só liquida o crédito a recuperar.
                </div>
              </>
            ))}
          {tipo && (
            <Campo rotulo="Descrição">
              {(id) => (
                <input
                  id={id}
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  maxLength={500}
                  placeholder={tipo === 'encargo' ? 'Ex.: IOF da fatura' : tipo === 'credito' ? 'Ex.: cashback do mês' : 'Ex.: estorno parcial do fornecedor'}
                  data-teste="descricao-lancamento"
                />
              )}
            </Campo>
          )}
          {erro && (
            <p className="aviso aviso-erro" role="alert" data-teste="erro-lancamento">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

/** Correção por novo registro ligado ao original (desfazer transporte ou vínculo): efeitos à vista e justificativa. */
function DesfazerComJustificativa({
  titulo,
  url,
  rotulo,
  teste,
  aoFechar,
  aoSalvar,
  children,
}: {
  titulo: string;
  url: string;
  rotulo: string;
  teste: string;
  aoFechar: () => void;
  aoSalvar: (r: unknown) => void;
  children: ReactNode;
}) {
  const [just, setJust] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  return (
    <Modal
      titulo={titulo}
      aoFechar={aoFechar}
      medio
      sobreposto
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button
            type="button"
            className="botao-primario"
            disabled={salvando || just.trim().length < 4}
            onClick={() => (
              setSalvando(true),
              void api
                .post(url, { justificativa: just })
                .then(aoSalvar)
                .catch((e) => setErro(erroDe(e)))
                .finally(() => setSalvando(false))
            )}
            data-teste={`confirmar-desfazer-${teste}`}
          >
            {rotulo}
          </button>
        </>
      }
    >
      <div className="form-pagamento" data-teste={`desfazer-${teste}`}>
        {children}
        <Campo rotulo="Justificativa">{(id) => <input id={id} value={just} onChange={(e) => setJust(e.target.value)} maxLength={500} data-teste={`justificativa-desfazer-${teste}`} />}</Campo>
        {erro && (
          <p className="aviso aviso-erro" role="alert">
            {erro}
          </p>
        )}
      </div>
    </Modal>
  );
}

function AnularLancamento({ lancamento: l, aoFechar, aoSalvar }: { lancamento: FichaFatura['lancamentos'][number]; aoFechar: () => void; aoSalvar: () => void }) {
  const [just, setJust] = useState('');
  const [erro, setErro] = useState('');
  return (
    <Modal
      titulo="Anular lançamento da fatura"
      aoFechar={aoFechar}
      medio
      sobreposto
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button
            type="button"
            className="botao-primario"
            disabled={just.trim().length < 4}
            onClick={() =>
              void api
                .post(`/api/contas-pagar/cartoes/lancamentos/${l.id}/anular`, {
                  justificativa: just,
                })
                .then(aoSalvar)
                .catch((e) => setErro(erroDe(e)))
            }
            data-teste="confirmar-anulacao"
          >
            Anular lançamento
          </button>
        </>
      }
    >
      <p>
        {l.descricao} · {rs(l.valor)}. A anulação é integral e fica no histórico (o lançamento original não é apagado).
      </p>
      <Campo rotulo="Justificativa">{(id) => <input id={id} value={just} onChange={(e) => setJust(e.target.value)} maxLength={500} data-teste="justificativa-anulacao" />}</Campo>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
    </Modal>
  );
}

/* ---------- C4: pagamento da fatura (escolhe a fatura paga; sugestão do mais antigo) e transporte com confirmação */
function PagarFatura({ ficha: f, aoFechar, aoSalvar }: { ficha: FichaFatura; aoFechar: () => void; aoSalvar: () => void }) {
  const apoio = useApoio();
  const a = apoio.dados;
  const faturas = f.faturasComSaldo;
  const [data, setData] = useState(hojeIso());
  const [contaId, setContaId] = useState('');
  const [meio, setMeio] = useState('boleto');
  const [ident, setIdent] = useState('');
  const [valor, setValor] = useState(deCentavos(faturas.reduce((s, x) => s + c(x.saldo), 0)));
  const [aplicar, setAplicar] = useState<Record<string, string> | null>(null);
  const [levar, setLevar] = useState(false);
  const [justLevar, setJustLevar] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [chave] = useState(novaChave);
  useEffect(() => {
    if (a && !contaId && a.bancos[0]) setContaId(a.bancos[0].id);
  }, [a, contaId]);
  const pago = centavosDe(valor) || 0;
  // sugestão: da fatura mais antiga para a mais nova, até o valor pago (editável por fatura)
  const sugestao = useMemo(() => {
    let resta = pago;
    return Object.fromEntries(
      faturas.map((x) => {
        const v = Math.min(c(x.saldo), resta);
        resta -= v;
        return [x.id, deCentavos(v)];
      }),
    );
  }, [faturas, pago]);
  const porFatura = aplicar ?? sugestao;
  const aplicado = faturas.reduce((s, x) => s + (centavosDe(porFatura[x.id] ?? '0') || 0), 0);
  const estaFatura = faturas.find((x) => x.id === f.fatura.id);
  const saldoDepois = estaFatura ? c(estaFatura.saldo) - (centavosDe(porFatura[estaFatura.id] ?? '0') || 0) : 0;
  const proxima = somarMes(f.fatura.mes, 1);
  const excede = faturas.some((x) => (centavosDe(porFatura[x.id] ?? '0') || 0) > c(x.saldo));
  const salvar = async () => {
    setErro('');
    setSalvando(true);
    try {
      await api.post('/api/contas-pagar/cartoes/pagamentos', {
        data_pagamento: data,
        valor,
        conta_bancaria_id: contaId,
        meio,
        identificador_transacao: ident || null,
        chave_idempotencia: chave,
        alocacoes: faturas.filter((x) => (centavosDe(porFatura[x.id] ?? '0') || 0) > 0).map((x) => ({ fatura_id: x.id, valor: porFatura[x.id] })),
      });
      if (levar && saldoDepois > 0) await api.post(`/api/contas-pagar/cartoes/faturas/${f.fatura.numero}/transportar`, { confirmar: true, justificativa: justLevar, destino_mes: proxima });
      aoSalvar();
    } catch (e) {
      setErro(erroDe(e));
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo={`Registrar pagamento da fatura — ${mesCurto(f.fatura.mes)}`}
      aoFechar={aoFechar}
      largo
      sobreposto
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button
            type="button"
            className="botao-primario"
            disabled={salvando || !a || pago <= 0 || aplicado !== pago || excede || (levar && justLevar.trim().length < 4)}
            title={aplicado !== pago ? 'O aplicado nas faturas precisa somar o valor pago.' : undefined}
            onClick={() => void salvar()}
            data-teste="confirmar-pagamento-fatura"
          >
            Registrar pagamento de {rs(pago / 100)}
          </button>
        </>
      }
    >
      {!a ? (
        <EstadoCarga carregando={apoio.carregando} erro={apoio.erro} recarregar={apoio.recarregar} />
      ) : (
        <div className="form-pagamento" data-teste="form-pagamento-fatura">
          <p className="faixa-resumo-modal">
            <span>
              Cobrado pelo emissor <strong>{rs(f.fatura.cobrado)}</strong>
            </span>
            {c(f.fatura.saldoAnteriorVinculo) > 0 && <span>+ saldo anterior {rs(f.fatura.saldoAnteriorVinculo)} (vínculo, devido na fatura original)</span>}
          </p>
          <div className="grade-form quatro">
            <CampoData rotulo="Data do pagamento" valor={data} aoMudar={setData} max={hojeIso()} obrigatorio teste="data-pagamento-fatura" />
            <Campo rotulo="Valor pago">{(id) => <input id={id} value={valor} onChange={(e) => (setValor(e.target.value), setAplicar(null))} inputMode="decimal" data-teste="valor-pagamento-fatura" />}</Campo>
            <Campo rotulo="Conta da Inttrix">
              {(id) => (
                <select id={id} value={contaId} onChange={(e) => setContaId(e.target.value)} data-teste="conta-pagamento-fatura">
                  {a.bancos.map((b) => (
                    <option key={b.id} value={b.id}>
                      {nomeBanco(b)}
                    </option>
                  ))}
                </select>
              )}
            </Campo>
            <Campo rotulo="Meio">
              {(id) => (
                <select id={id} value={meio} onChange={(e) => setMeio(e.target.value)} data-teste="meio-pagamento-fatura">
                  {Object.entries(MEIOS).map(([k, n]) => (
                    <option key={k} value={k}>
                      {n}
                    </option>
                  ))}
                </select>
              )}
            </Campo>
          </div>
          <Campo rotulo="Identificador da transação">{(id) => <input id={id} value={ident} onChange={(e) => setIdent(e.target.value)} maxLength={120} data-teste="identificador-pagamento-fatura" />}</Campo>
          <p className="texto-secundario sem-margem">Sugestão: da fatura mais antiga para a mais nova. Ajuste qual fatura foi efetivamente paga.</p>
          <div className="rolagem">
            <table className="tabela tabela-compacta" data-teste="aplicar-faturas">
              <thead>
                <tr>
                  <th>Obrigação</th>
                  <th className="numero">Saldo antes</th>
                  <th className="numero">Aplicar agora</th>
                  <th className="numero">Saldo depois</th>
                </tr>
              </thead>
              <tbody>
                {faturas.map((x) => {
                  const v = centavosDe(porFatura[x.id] ?? '0') || 0;
                  return (
                    <tr key={x.id}>
                      <td>
                        Fatura {mesCurto(x.mes)}
                        <small className="bloco texto-secundario">
                          {x.numero}
                          {x.id !== f.fatura.id && x.mes < f.fatura.mes ? ' · saldo anterior, mais antigo' : ''}
                        </small>
                      </td>
                      <td className="numero">{rs(x.saldo)}</td>
                      <td className="numero">
                        <input
                          className="valor-aplicar"
                          value={porFatura[x.id] ?? ''}
                          onChange={(e) => setAplicar({ ...porFatura, [x.id]: e.target.value })}
                          inputMode="decimal"
                          aria-label={`Aplicar em ${x.numero}`}
                          data-teste={`aplicar-${x.numero}`}
                        />
                      </td>
                      <td className="numero">{rs(Math.max(c(x.saldo) - v, 0) / 100)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="faixa-resumo-modal">
            <span>
              Pagamento <strong>{rs(pago / 100)}</strong>
            </span>
            <span>
              Aplicado <strong>{rs(aplicado / 100)}</strong> {aplicado === pago && pago > 0 ? <span className="texto-confere">✓</span> : <span className="texto-diferenca">≠ pago</span>}
            </span>
            {estaFatura && (
              <span>
                Saldo da fatura {mesCurto(f.fatura.mes)} <strong>{rs(Math.max(saldoDepois, 0) / 100)}</strong>
              </span>
            )}
          </p>
          {estaFatura && saldoDepois > 0 && (
            <>
              <label className={`caixa-destaque${levar ? ' marcada' : ''}`} data-teste="levar-adiante">
                <input type="checkbox" checked={levar} onChange={(e) => setLevar(e.target.checked)} />
                <span>
                  <strong>
                    Levar o saldo de {rs(saldoDepois / 100)} para a fatura {mesCurto(proxima)} (vínculo)
                  </strong>
                  <small>
                    A dívida continua em {mesCurto(f.fatura.mes)} e aparece em {mesCurto(proxima)} como saldo anterior, sem somar duas vezes. Só com a sua confirmação.
                  </small>
                </span>
              </label>
              {levar && (
                <Campo rotulo="Justificativa do transporte">
                  {(id) => <input id={id} value={justLevar} onChange={(e) => setJustLevar(e.target.value)} maxLength={500} placeholder="Ex.: pago o mínimo" data-teste="justificativa-levar" />}
                </Campo>
              )}
            </>
          )}
          <p className="texto-secundario sem-margem">Encargos do atraso, se o emissor cobrar, entram na fatura seguinte por “Informar encargo ou crédito”, sem cálculo.</p>
          {erro && (
            <p className="aviso aviso-erro" role="alert" data-teste="erro-pagamento-fatura">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

function TransportarSaldo({ ficha: f, aoFechar, aoSalvar }: { ficha: FichaFatura; aoFechar: () => void; aoSalvar: () => void }) {
  const livre = c(f.fatura.saldo) - c(f.fatura.levadoAdiante);
  const [valor, setValor] = useState(deCentavos(Math.max(livre, 0)));
  const [destino, setDestino] = useState(somarMes(f.fatura.mes, 1));
  const [just, setJust] = useState('');
  const [confirmo, setConfirmo] = useState(false);
  const [erro, setErro] = useState('');
  return (
    <Modal
      titulo={`Levar saldo adiante — fatura ${mesCurto(f.fatura.mes)}`}
      aoFechar={aoFechar}
      medio
      sobreposto
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button
            type="button"
            className="botao-primario"
            disabled={!confirmo || just.trim().length < 4 || livre <= 0}
            onClick={() =>
              void api
                .post(`/api/contas-pagar/cartoes/faturas/${f.fatura.numero}/transportar`, {
                  confirmar: true,
                  valor,
                  destino_mes: destino,
                  justificativa: just,
                })
                .then(aoSalvar)
                .catch((e) => setErro(erroDe(e)))
            }
            data-teste="confirmar-transporte"
          >
            Levar saldo adiante
          </button>
        </>
      }
    >
      {livre <= 0 ? (
        <p>Não há saldo desta fatura a levar adiante (já pago ou já transportado).</p>
      ) : (
        <div className="form-pagamento">
          <div className="grade-form duas">
            <Campo rotulo="Valor levado">{(id) => <input id={id} value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal" data-teste="valor-transporte" />}</Campo>
            <Campo rotulo="Para a fatura">
              {(id) => (
                <select id={id} value={destino} onChange={(e) => setDestino(e.target.value)} data-teste="destino-transporte">
                  {[1, 2, 3].map((k) => (
                    <option key={k} value={somarMes(f.fatura.mes, k)}>
                      {mesCurto(somarMes(f.fatura.mes, k))}
                    </option>
                  ))}
                </select>
              )}
            </Campo>
          </div>
          <Campo rotulo="Justificativa">{(id) => <input id={id} value={just} onChange={(e) => setJust(e.target.value)} maxLength={500} data-teste="justificativa-transporte" />}</Campo>
          <label className="caixa-destaque">
            <input type="checkbox" checked={confirmo} onChange={(e) => setConfirmo(e.target.checked)} data-teste="confirmo-transporte" />
            <span>
              <strong>Confirmo o transporte por vínculo</strong>
              <small>A dívida continua na fatura {mesCurto(f.fatura.mes)}; a fatura de destino mostra o saldo anterior, sem somar duas vezes.</small>
            </span>
          </label>
          {erro && (
            <p className="aviso aviso-erro" role="alert">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ====================================================================== aba Reembolsos (R1) */
export function AbaReembolsos({ abas }: { abas: ReactNode }) {
  const { pode } = useSessao();
  const carga = useCarga(() => api.get<ListaReembolsos>('/api/contas-pagar/reembolsos'), []);
  const [marcados, setMarcados] = useState<Record<string, string[]>>({});
  const [aberto, setAberto] = useState<string | null>(null);
  const [erro, setErro] = useState('');
  const d = carga.dados;
  const r = d?.resumo;
  // pessoas com valores a reembolsar e as que só têm pagamentos sem reembolso (mostrados, sem ação)
  const blocos = useMemo(() => {
    if (!d) return [];
    const m = new Map<
      string,
      {
        pessoa: { id: string; nome: string; comUsuario: boolean };
        aReembolsar: string;
        itens: ItemPessoa[];
        sem: ItemPessoa[];
      }
    >();
    for (const p of d.pessoas) m.set(p.pessoa.id, { ...p, sem: [] });
    for (const x of d.semReembolso) {
      const b = m.get(x.pessoa.id) ?? {
        pessoa: x.pessoa,
        aReembolsar: '0',
        itens: [],
        sem: [],
      };
      b.sem.push(x);
      m.set(x.pessoa.id, b);
    }
    return [...m.values()].sort((a, b) => c(b.aReembolsar) - c(a.aReembolsar) || a.pessoa.nome.localeCompare(b.pessoa.nome));
  }, [d]);
  const agrupar = async (pessoaId: string) => {
    setErro('');
    try {
      const x = await api.post<{ reembolso: string }>('/api/contas-pagar/reembolsos', { pessoa_id: pessoaId, pagamentos: marcados[pessoaId] ?? [] });
      setMarcados({ ...marcados, [pessoaId]: [] });
      await carga.recarregar();
      setAberto(x.reembolso);
    } catch (e) {
      setErro(erroDe(e));
    }
  };
  return (
    <>
      <h1 className="titulo-pagina rotulo-oculto">Contas a Pagar · Reembolsos</h1>
      {r && (
        <CardsLista n={4} teste="cards-reembolsos">
          <CardLista
            icone="alerta"
            tom="vermelho"
            rotulo="A reembolsar"
            valor={rs(r.aReembolsar.valor)}
            detalhe={`${plural(r.aReembolsar.pessoas, 'pessoa', 'pessoas')} · fora de reembolso`}
            ajuda={<Ajuda titulo="A reembolsar">Pagamentos feitos por pessoas pela Inttrix que geram reembolso e ainda não foram agrupados num reembolso.</Ajuda>}
            teste="card-a-reembolsar"
          />
          <CardLista
            icone="relogio"
            tom="roxo"
            rotulo="Em reembolsos abertos"
            valor={rs(r.emAbertos.valor)}
            detalhe={r.emAbertos.numeros.length ? r.emAbertos.numeros.slice(0, 3).join(' · ') : 'nenhum'}
            teste="card-reembolsos-abertos"
          />
          <CardLista icone="documento" tom="azul" rotulo="Reembolsado no mês" valor={rs(r.reembolsadoNoMes.valor)} detalhe="saída da conta da Inttrix" teste="card-reembolsado-mes" />
          <CardLista icone="dinheiro" tom="verde" rotulo="Sem reembolso" valor={plural(r.semReembolso.quantidade, 'pagamento', 'pagamentos')} detalhe="já acertado · com justificativa" teste="card-sem-reembolso" />
        </CardsLista>
      )}
      <div className="linha-abas-acao">{abas}</div>
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-reembolsos">
          {erro}
        </p>
      )}
      {!d ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="reembolsos" />
      ) : (
        <div className="conteudo-reembolsos">
          {blocos.length === 0 && (
            <p className="vazio" data-teste="reembolsos-vazio">
              Nenhum valor a reembolsar. Quando uma pessoa pagar uma despesa pela Inttrix, registre em “Registrar pagamento › Pessoa que pagou”.
            </p>
          )}
          {blocos.map((b) => {
            const sel = marcados[b.pessoa.id] ?? [];
            const total = b.itens.filter((x) => sel.includes(x.id)).reduce((s, x) => s + c(x.valor), 0);
            return (
              <section key={b.pessoa.id} className="bloco-pessoa-fin" data-teste={`pessoa-${b.pessoa.nome}`}>
                <h3>
                  <span>
                    {b.pessoa.nome} <small className="texto-secundario">· pessoa financeira · {b.pessoa.comUsuario ? 'com usuário no HUB' : 'sem usuário no HUB'}</small>
                  </span>
                  <span>
                    a reembolsar <strong>{rs(b.aReembolsar)}</strong>
                  </span>
                </h3>
                <div className="rolagem">
                  <table className="tabela tabela-compacta">
                    <thead>
                      <tr>
                        <th className="col-selecao">
                          <span className="rotulo-oculto">Selecionar</span>
                        </th>
                        <th>Pago em</th>
                        <th>Despesa</th>
                        <th>Favorecido</th>
                        <th>Documento</th>
                        <th className="numero">Valor</th>
                        <th>Reembolso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...b.itens, ...b.sem].map((x) => (
                        <tr key={x.id} className={x.escolha === 'sem' ? 'linha-estornada' : undefined}>
                          <td className="col-selecao">
                            <input
                              type="checkbox"
                              disabled={x.escolha === 'sem' || !pode('lancar', 'conta_pagar')}
                              checked={sel.includes(x.id)}
                              onChange={(e) =>
                                setMarcados({
                                  ...marcados,
                                  [b.pessoa.id]: e.target.checked ? [...sel, x.id] : sel.filter((y) => y !== x.id),
                                })
                              }
                              aria-label={`Agrupar ${x.descricao}`}
                              data-teste={`marcar-${x.numero}`}
                            />
                          </td>
                          <td className="sem-quebra">{dataBr(x.data)}</td>
                          <td>
                            <a href={`#/contas-pagar/${x.obrigacao}`} className="link-forte">
                              {x.descricao}
                            </a>
                          </td>
                          <td>{x.favorecido}</td>
                          <td>{x.documento ?? (x.comArquivo ? 'arquivo' : <small className="texto-secundario">pendente</small>)}</td>
                          <td className="numero">{rs(x.valor)}</td>
                          <td>{x.escolha === 'sem' ? <small className="texto-secundario">não há valor a reembolsar · “{x.justificativaSemReembolso}”</small> : <span className="chip-fin">gera reembolso</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {b.itens.length > 0 && pode('lancar', 'conta_pagar') && (
                  <div className="acoes-bloco">
                    <button type="button" className="botao-primario" disabled={!sel.length} onClick={() => void agrupar(b.pessoa.id)} data-teste={`agrupar-${b.pessoa.nome}`}>
                      {sel.length ? `Agrupar em reembolso (${plural(sel.length, 'item', 'itens')} · ${rs(total / 100)})` : 'Agrupar em reembolso'}
                    </button>
                  </div>
                )}
              </section>
            );
          })}
          <h3 className="titulo-secao-fin">Reembolsos</h3>
          {d.reembolsos.length === 0 ? (
            <p className="texto-secundario">Nenhum reembolso agrupado.</p>
          ) : (
            <RolagemIndicada className="rolagem quadro-tabela" teste="reembolsos">
              <table className="tabela" data-teste="lista-reembolsos">
                <thead>
                  <tr>
                    <th>Reembolso</th>
                    <th>Pessoa</th>
                    <th className="numero">Itens</th>
                    <th className="numero">Total</th>
                    <th className="numero">Reembolsado</th>
                    <th className="numero">Saldo</th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {d.reembolsos.map((x) => (
                    <tr key={x.id} data-teste={`rb-${x.numero}`}>
                      <td>
                        <button type="button" className="botao-link link-forte" onClick={() => setAberto(x.numero)} data-teste={`abrir-${x.numero}`}>
                          {x.numero}
                        </button>
                      </td>
                      <td>{x.pessoa.nome}</td>
                      <td className="numero">{x.itens}</td>
                      <td className="numero">{rs(x.total)}</td>
                      <td className="numero">{rs(x.reembolsado)}</td>
                      <td className="numero">{rs(x.saldo)}</td>
                      <td>
                        <SeloSituacao texto={SITUACAO_RB[x.situacao].texto} tom={SITUACAO_RB[x.situacao].tom} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </RolagemIndicada>
          )}
        </div>
      )}
      {aberto && <FichaReembolsoModal numero={aberto} aoFechar={() => (setAberto(null), void carga.recarregar())} />}
    </>
  );
}

function FichaReembolsoModal({ numero, aoFechar }: { numero: string; aoFechar: () => void }) {
  const { pode } = useSessao();
  const carga = useCarga(() => api.get<FichaReembolso>(`/api/contas-pagar/reembolsos/${encodeURIComponent(numero)}`), [numero]);
  const [pagar, setPagar] = useState(false);
  const [cancelar, setCancelar] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState('');
  const [desfazer, setDesfazer] = useState<FichaReembolso['pagamentos'][number] | null>(null);
  const f = carga.dados;
  const rbv = f?.reembolso;
  return (
    <Modal
      titulo={`Reembolso ${numero}${rbv ? ` · ${rbv.pessoa.nome}` : ''}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Fechar
          </button>
          {rbv && rbv.situacao === 'aberto' && pode('cancelar', 'conta_pagar') && (
            <button type="button" className="botao-secundario" onClick={() => setCancelar(true)} data-teste="cancelar-reembolso">
              Cancelar reembolso
            </button>
          )}
          {rbv && c(rbv.saldo) > 0 && pode('registrar', 'pagamento') && (
            <button type="button" className="botao-primario" onClick={() => setPagar(true)} data-teste="pagar-reembolso">
              Registrar pagamento
            </button>
          )}
        </>
      }
    >
      {!f || !rbv ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} />
      ) : (
        <div className="form-pagamento" data-teste="ficha-reembolso">
          <p className="faixa-resumo-modal">
            <span>
              Total <strong>{rs(rbv.total)}</strong>
            </span>
            <span>
              Reembolsado <strong>{rs(rbv.reembolsado)}</strong>
            </span>
            <span>
              Saldo <strong>{rs(rbv.saldo)}</strong>
            </span>
            <SeloSituacao texto={SITUACAO_RB[rbv.situacao].texto} tom={SITUACAO_RB[rbv.situacao].tom} />
          </p>
          {rbv.cancelamento && <p className="aviso aviso-atencao-fin">Cancelado: {rbv.cancelamento.motivo}</p>}
          <div className="rolagem">
            <table className="tabela tabela-compacta">
              <thead>
                <tr>
                  <th>Pago em</th>
                  <th>Despesa</th>
                  <th className="numero">Valor</th>
                  <th className="numero">Já reembolsado</th>
                  <th className="numero">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {f.itens.map((i) => (
                  <tr key={i.id}>
                    <td className="sem-quebra">{dataBr(i.data)}</td>
                    <td>
                      <a href={`#/contas-pagar/${i.obrigacao}`} className="link-forte">
                        {i.descricao}
                      </a>
                      <small className="bloco texto-secundario">{i.favorecido}</small>
                    </td>
                    <td className="numero">{rs(i.valor)}</td>
                    <td className="numero">{rs(i.reembolsado)}</td>
                    <td className="numero">{rs(i.saldo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {f.pagamentos.length > 0 && (
            <section className="quadro-form">
              <h3>Pagamentos do reembolso</h3>
              <table className="tabela tabela-compacta">
                <tbody>
                  {f.pagamentos.map((p) => (
                    <tr key={p.alocacaoId} className={p.estorno || p.desfeito ? 'linha-estornada' : undefined} data-teste={`vinculo-${p.numero}`}>
                      <td>{p.numero}</td>
                      <td className="sem-quebra">{dataBr(p.data)}</td>
                      <td>
                        {p.conta}
                        <small className="bloco texto-secundario">
                          {MEIOS[p.meio] ?? p.meio}
                          {p.identificador ? ` · ${p.identificador}` : ''}
                        </small>
                        {p.desfeito && <small className="bloco texto-secundario">vínculo desfeito: {p.desfeito.justificativa}</small>}
                      </td>
                      <td className="numero">
                        {rs(p.aplicado)}
                        {p.compartilhado && <small className="bloco texto-secundario">de {rs(p.valor)} (vínculo)</small>}
                      </td>
                      <td>
                        {!p.estorno && !p.desfeito && pode('estornar', 'pagamento') && (
                          <button type="button" className="botao-link" onClick={() => setDesfazer(p)} data-teste={`desfazer-vinculo-${p.numero}`}>
                            Desfazer vínculo
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
          {cancelar && (
            <section className="quadro-form">
              <Campo rotulo="Motivo do cancelamento">{(id) => <input id={id} value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500} data-teste="motivo-cancelar-reembolso" />}</Campo>
              <div className="acoes-bloco">
                <button
                  type="button"
                  className="botao-primario"
                  disabled={motivo.trim().length < 4}
                  onClick={() =>
                    void api
                      .post(`/api/contas-pagar/reembolsos/${numero}/cancelar`, {
                        motivo,
                      })
                      .then(aoFechar)
                      .catch((e) => setErro(erroDe(e)))
                  }
                  data-teste="confirmar-cancelar-reembolso"
                >
                  Cancelar reembolso
                </button>
              </div>
              <p className="texto-secundario sem-margem">Os itens voltam a “a reembolsar”. Com pagamento ativo, estorne o pagamento antes.</p>
            </section>
          )}
          {erro && (
            <p className="aviso aviso-erro" role="alert">
              {erro}
            </p>
          )}
        </div>
      )}
      {pagar && f && <PagarReembolso ficha={f} aoFechar={() => setPagar(false)} aoSalvar={() => (setPagar(false), void carga.recarregar())} />}
      {desfazer && rbv && (
        <DesfazerComJustificativa
          titulo="Desfazer vínculo do reembolso"
          url={`/api/contas-pagar/reembolsos/vinculos/${desfazer.alocacaoId}/desfazer`}
          rotulo="Desfazer só este vínculo"
          teste="vinculo"
          aoFechar={() => setDesfazer(null)}
          aoSalvar={() => (setDesfazer(null), void carga.recarregar())}
        >
          <p>
            Vínculo de {rs(desfazer.aplicado)} do pagamento {desfazer.numero} ({dataBr(desfazer.data)}
            {desfazer.compartilhado ? `, de ${rs(desfazer.valor)}` : ''}) com {rbv.numero}.
          </p>
          <ul className="lista-efeitos">
            <li>O pagamento bancário fica como está (não é estorno){desfazer.compartilhado ? ' e os outros vínculos dele continuam' : ''}.</li>
            <li>
              {rs(desfazer.aplicado)} voltam a ficar disponíveis no pagamento {desfazer.numero} e devidos em {rbv.numero}.
            </li>
            <li>O vínculo original fica no histórico, marcado como desfeito. Se o próprio pagamento estiver errado, use o estorno.</li>
          </ul>
        </DesfazerComJustificativa>
      )}
    </Modal>
  );
}

/* ---------- R2: pagamento parcial do reembolso com distribuição por item (decisão 3 A) */
function PagarReembolso({ ficha: f, aoFechar, aoSalvar }: { ficha: FichaReembolso; aoFechar: () => void; aoSalvar: () => void }) {
  const apoio = useApoio();
  const a = apoio.dados;
  const [data, setData] = useState(hojeIso());
  const [contaId, setContaId] = useState('');
  const [meio, setMeio] = useState('pix');
  const [ident, setIdent] = useState('');
  const [valor, setValor] = useState(deCentavos(c(f.reembolso.saldo)));
  const [editado, setEditado] = useState<Record<string, string> | null>(null);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [chave] = useState(novaChave);
  // pagamento novo pela conta × vínculo a um pagamento já registrado à pessoa (ex.: corrigir um vínculo desfeito)
  const [modo, setModo] = useState<'novo' | 'vinculo'>('novo');
  const [vinculoId, setVinculoId] = useState('');
  const disp = useCarga(async () => (modo === 'vinculo' ? api.get<Disponivel[]>(`/api/contas-pagar/reembolsos/pagamentos-disponiveis?pessoa_id=${f.reembolso.pessoa.id}`) : []), [modo, f.reembolso.pessoa.id]);
  useEffect(() => {
    if (a && !contaId && a.bancos[0]) setContaId(a.bancos[0].id);
  }, [a, contaId]);
  const pago = centavosDe(valor) || 0;
  const sugestao = useMemo(() => {
    let resta = pago;
    return Object.fromEntries(
      f.itens.map((i) => {
        const v = Math.min(c(i.saldo), resta);
        resta -= v;
        return [i.id, deCentavos(v)];
      }),
    );
  }, [f.itens, pago]);
  const dist = editado ?? sugestao;
  const aplicado = f.itens.reduce((s, i) => s + (centavosDe(dist[i.id] ?? '0') || 0), 0);
  const excede = f.itens.some((i) => (centavosDe(dist[i.id] ?? '0') || 0) > c(i.saldo));
  const salvar = async () => {
    setErro('');
    setSalvando(true);
    try {
      await api.post(`/api/contas-pagar/reembolsos/${f.reembolso.numero}/pagamentos`, {
        ...(modo === 'vinculo' ? { vinculo: { pagamento_id: vinculoId } } : { data_pagamento: data, conta_bancaria_id: contaId, meio, identificador_transacao: ident || null, chave_idempotencia: chave }),
        valor,
        distribuicao: f.itens.map((i) => ({
          item_id: i.id,
          valor: dist[i.id] || '0',
        })),
      });
      aoSalvar();
    } catch (e) {
      setErro(erroDe(e));
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo={`Registrar pagamento do reembolso — ${f.reembolso.numero}`}
      aoFechar={aoFechar}
      largo
      sobreposto
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button
            type="button"
            className="botao-primario"
            disabled={salvando || !a || pago <= 0 || aplicado !== pago || excede || pago > c(f.reembolso.saldo) || (modo === 'vinculo' && !vinculoId)}
            onClick={() => void salvar()}
            data-teste="confirmar-pagamento-reembolso"
          >
            {modo === 'vinculo' ? `Vincular ${rs(pago / 100)}` : `Registrar pagamento de ${rs(pago / 100)}`}
          </button>
        </>
      }
    >
      {!a ? (
        <EstadoCarga carregando={apoio.carregando} erro={apoio.erro} recarregar={apoio.recarregar} />
      ) : (
        <div className="form-pagamento" data-teste="form-pagamento-reembolso">
          <p className="faixa-resumo-modal">
            <span>{f.reembolso.pessoa.nome}</span>
            <span>
              Total <strong>{rs(f.reembolso.total)}</strong>
            </span>
            <span>
              Já reembolsado <strong>{rs(f.reembolso.reembolsado)}</strong>
            </span>
            <span>
              Saldo <strong>{rs(f.reembolso.saldo)}</strong>
            </span>
          </p>
          <div className="linha-opcoes" data-teste="modo-pagamento-reembolso">
            <label className="caixa-marcar">
              <input type="radio" name="modo-rb" checked={modo === 'novo'} onChange={() => setModo('novo')} data-teste="rb-novo" />
              <span>Novo pagamento pela conta da Inttrix</span>
            </label>
            <label className="caixa-marcar">
              <input type="radio" name="modo-rb" checked={modo === 'vinculo'} onChange={() => setModo('vinculo')} data-teste="rb-vinculo" />
              <span>Vincular pagamento já registrado à pessoa (sem nova saída)</span>
            </label>
          </div>
          {modo === 'novo' ? (
            <>
              <div className="grade-form quatro">
                <CampoData rotulo="Data do pagamento" valor={data} aoMudar={setData} max={hojeIso()} obrigatorio teste="data-pagamento-reembolso" />
                <Campo rotulo="Valor pago">{(id) => <input id={id} value={valor} onChange={(e) => (setValor(e.target.value), setEditado(null))} inputMode="decimal" data-teste="valor-pagamento-reembolso" />}</Campo>
                <Campo rotulo="Conta da Inttrix">
                  {(id) => (
                    <select id={id} value={contaId} onChange={(e) => setContaId(e.target.value)} data-teste="conta-pagamento-reembolso">
                      {a.bancos.map((b) => (
                        <option key={b.id} value={b.id}>
                          {nomeBanco(b)}
                        </option>
                      ))}
                    </select>
                  )}
                </Campo>
                <Campo rotulo="Meio">
                  {(id) => (
                    <select id={id} value={meio} onChange={(e) => setMeio(e.target.value)} data-teste="meio-pagamento-reembolso">
                      {Object.entries(MEIOS).map(([k, n]) => (
                        <option key={k} value={k}>
                          {n}
                        </option>
                      ))}
                    </select>
                  )}
                </Campo>
              </div>
              <Campo rotulo="Identificador da transação">{(id) => <input id={id} value={ident} onChange={(e) => setIdent(e.target.value)} maxLength={120} data-teste="identificador-pagamento-reembolso" />}</Campo>
            </>
          ) : (
            <>
              {(disp.dados ?? []).length === 0 ? (
                <p className="texto-secundario" data-teste="rb-sem-disponiveis">
                  Nenhum pagamento à pessoa com saldo disponível para vínculo.
                </p>
              ) : (
                <div className="rolagem">
                  <table className="tabela tabela-compacta" data-teste="rb-disponiveis">
                    <thead>
                      <tr>
                        <th />
                        <th>Pagamento</th>
                        <th>Conta</th>
                        <th className="numero">Valor</th>
                        <th className="numero">Disponível</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(disp.dados ?? []).map((x) => (
                        <tr key={x.id} title={x.identificador ?? undefined}>
                          <td>
                            <input
                              type="radio"
                              name="rb-vinculo"
                              checked={vinculoId === x.id}
                              onChange={() => (setVinculoId(x.id), setValor(deCentavos(Math.min(c(x.disponivel), c(f.reembolso.saldo)))), setEditado(null))}
                              aria-label={`Vincular ${x.numero}`}
                              data-teste={`rb-vincular-${x.numero}`}
                            />
                          </td>
                          <td>
                            {x.numero}
                            <small className="bloco texto-secundario sem-quebra">
                              {dataBr(x.data)} · {MEIOS[x.meio] ?? x.meio}
                            </small>
                          </td>
                          <td className="sem-quebra">{x.conta.split(' · ').slice(-1)[0]}</td>
                          <td className="numero">{rs(x.valor)}</td>
                          <td className="numero">
                            <strong>{rs(x.disponivel)}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="grade-form duas">
                <Campo rotulo="Valor do vínculo">{(id) => <input id={id} value={valor} onChange={(e) => (setValor(e.target.value), setEditado(null))} inputMode="decimal" data-teste="valor-vinculo-reembolso" />}</Campo>
              </div>
            </>
          )}
          <p className="texto-secundario sem-margem">Sugestão: da despesa mais antiga para a mais nova. Ajuste por item antes de confirmar.</p>
          <div className="rolagem">
            <table className="tabela tabela-compacta" data-teste="distribuicao-reembolso">
              <thead>
                <tr>
                  <th>Pago em</th>
                  <th>Despesa</th>
                  <th className="numero">Valor</th>
                  <th className="numero">Já reembolsado</th>
                  <th className="numero">Aplicar agora</th>
                  <th className="numero">Saldo restante</th>
                </tr>
              </thead>
              <tbody>
                {f.itens.map((i) => {
                  const v = centavosDe(dist[i.id] ?? '0') || 0;
                  return (
                    <tr key={i.id}>
                      <td className="sem-quebra">{dataBr(i.data)}</td>
                      <td>{i.descricao}</td>
                      <td className="numero">{rs(i.valor)}</td>
                      <td className="numero">{rs(i.reembolsado)}</td>
                      <td className="numero">
                        {c(i.saldo) === 0 ? (
                          <small className="texto-secundario">quitado</small>
                        ) : (
                          <input
                            className="valor-aplicar"
                            value={dist[i.id] ?? ''}
                            onChange={(e) => setEditado({ ...dist, [i.id]: e.target.value })}
                            inputMode="decimal"
                            aria-label={`Aplicar em ${i.descricao}`}
                            data-teste={`aplicar-item-${i.pagamento}`}
                          />
                        )}
                      </td>
                      <td className="numero">{rs(Math.max(c(i.saldo) - v, 0) / 100)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="faixa-resumo-modal">
            <span>
              Pagamento <strong>{rs(pago / 100)}</strong>
            </span>
            <span>
              Aplicado <strong>{rs(aplicado / 100)}</strong> {aplicado === pago && pago > 0 ? <span className="texto-confere">✓ = pagamento</span> : <span className="texto-diferenca">≠ pagamento</span>}
            </span>
            <span>
              Saldo do reembolso depois <strong>{rs(Math.max(c(f.reembolso.saldo) - pago, 0) / 100)}</strong>
            </span>
          </p>
          <p className="texto-secundario sem-margem" data-teste="rodape-pagamento-reembolso">
            {modo === 'vinculo'
              ? 'Nenhuma saída nova da conta da Inttrix; este vínculo utiliza um pagamento já registrado.'
              : 'Uma saída da conta da Inttrix.'}{' '}
            As despesas não mudam (já foram pagas pela pessoa); pagamentos anteriores do reembolso ficam preservados.
          </p>
          {erro && (
            <p className="aviso aviso-erro" role="alert" data-teste="erro-pagamento-reembolso">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
